// src/components/FileUpload.tsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle, FolderOpen, Eye, ArrowRight, Download } from 'lucide-react';
import Papa from 'papaparse';
import { AccountEntry } from '../../types';

// Asset imports
import restaurantTrialBalanceImg from '../../assets/restaurant_trial_balance.png';
import restaurantPLImg from '../../assets/restaurant_p&l.png';
import restaurantBalanceSheetImg from '../../assets/restaurant_balance_sheet.png';

// ADD THESE IMPORTS
import { ProcessingStep } from './ProcessingStep';
import { SuccessStep } from './SuccessStep';
import { analyzeDataConflicts, mergeDataWithConflictResolution } from '../../lib/data/conflictAnalysis';
import { ConflictResolution } from './ConflictResolution';

interface ConsolidationConfig {
  masterFileStrategy: 'most_recent' | 'most_comprehensive' | 'trial_balance_priority';
  duplicateResolution: 'merge' | 'suffix_context' | 'keep_all';
}

const CONSOLIDATION_CONFIG: ConsolidationConfig = {
  masterFileStrategy: 'trial_balance_priority',
  duplicateResolution: 'merge'
};

class SmartAccountConsolidationEngine {
  private accountKeyToNumber = new Map<string, string>();
  private canonicalAccountNames = new Map<string, string>();
  private accountNumberCounters = new Map<string, number>();
  private preservedTrialBalanceAccounts = new Set<string>();
  private usedAccountNumbers = new Set<string>();
  private dynamicRanges = new Map<string, { start: number; end: number }>();
  private fallbackCounter = 90000;
  
  constructor(private config: ConsolidationConfig) {}

  private normalizeAccountNameForMatching(accountName: string): string {
    return accountName.toLowerCase().trim().replace(/\s+/g, ' ');
  }

  private registerAssignedAccount(accountKey: string, accountNumber: string): void {
    const normalizedKey = this.normalizeAccountNameForMatching(accountKey);
    this.accountKeyToNumber.set(normalizedKey, accountNumber);
    if (!this.canonicalAccountNames.has(normalizedKey)) {
      this.canonicalAccountNames.set(normalizedKey, accountKey);
    }
    this.usedAccountNumbers.add(accountNumber);
  }

  private getNextFallbackNumber(): string {
    while (this.usedAccountNumbers.has(this.fallbackCounter.toString())) {
      this.fallbackCounter += 1;
    }
    const nextFallback = this.fallbackCounter.toString();
    this.fallbackCounter += 1;
    return nextFallback;
  }

  seedExistingAccounts(existingData: AccountEntry[]): void {
    existingData.forEach((entry) => {
      const accountNumber = entry.accountNumber?.toString().trim();
      const accountName = entry.name?.toString().trim();
      if (!accountNumber || !accountName) return;

      this.registerAssignedAccount(accountName, accountNumber);
      const parsedNumber = parseInt(accountNumber, 10);
      if (!isNaN(parsedNumber)) {
        this.fallbackCounter = Math.max(this.fallbackCounter, parsedNumber + 1);
      }
    });

    console.log(`♻️ Seeded ${this.accountKeyToNumber.size} existing account mappings`);
  }
  
  // Phase 1: Analyze account number landscape and extract file dates
  analyzeAccountNumberSpace(files: FileWithPreview[]): void {
    console.log('🔍 Phase 1: Analyzing account number space and file recency...');
    
    // First, extract the most recent date and detect period type from each file
    files.forEach(file => {
      if (file.fullData) {
        file.mostRecentDate = this.extractMostRecentDate(file);
        
        // Detect period type
        const [headers, ...rows] = file.fullData;
        const periodDetection = this.detectPeriodType(headers, rows);
        file.periodType = periodDetection.periodType;
        file.periodDetectionConfidence = periodDetection.confidence;
        file.detectedPeriods = periodDetection.detectedPeriods;
        file.periodPattern = periodDetection.periodPattern;
        
        console.log(`📊 Period detection for ${file.file.name}:`, {
          type: periodDetection.periodType,
          confidence: periodDetection.confidence,
          pattern: periodDetection.periodPattern,
          periods: periodDetection.detectedPeriods.slice(0, 3), // Show first 3 examples
          warnings: periodDetection.warnings
        });
      }
    });
    
    let trialBalanceAccounts: number[] = [];
    
    files.forEach(file => {
      if (getEffectiveFormat(file)?.includes('Trial Balance')) {
        const accounts = this.extractTrialBalanceAccountNumbers(file);
        trialBalanceAccounts = [...trialBalanceAccounts, ...accounts];
      }
    });
    
    if (trialBalanceAccounts.length > 0) {
      const minTB = Math.min(...trialBalanceAccounts);
      const maxTB = Math.max(...trialBalanceAccounts);
      
      console.log(`📊 Trial Balance accounts range: ${minTB} - ${maxTB}`);
      
      // Preserve trial balance range exactly
      this.dynamicRanges.set('TRIAL_BALANCE', { start: minTB, end: maxTB });
      trialBalanceAccounts.forEach(num => {
        const accountNum = num.toString();
        this.preservedTrialBalanceAccounts.add(accountNum);
        this.usedAccountNumbers.add(accountNum);
        this.fallbackCounter = Math.max(this.fallbackCounter, num + 1);
      });
      
      // Find safe starting point for synthetic accounts
      const safeStart = Math.max(maxTB + 1000, 10000);
      
      this.dynamicRanges.set('BALANCE_SHEET_ASSETS', { start: safeStart, end: safeStart + 999 });
      this.dynamicRanges.set('BALANCE_SHEET_LIABILITIES', { start: safeStart + 1000, end: safeStart + 1999 });
      this.dynamicRanges.set('BALANCE_SHEET_EQUITY', { start: safeStart + 2000, end: safeStart + 2999 });
      this.dynamicRanges.set('PL_REVENUE', { start: safeStart + 3000, end: safeStart + 3999 });
      this.dynamicRanges.set('PL_EXPENSES', { start: safeStart + 4000, end: safeStart + 6999 });
      
    } else {
      // No trial balance - use default ranges
      const defaultRanges = {
        BALANCE_SHEET_ASSETS: { start: 4000, end: 4999 },
        BALANCE_SHEET_LIABILITIES: { start: 5000, end: 5999 },
        BALANCE_SHEET_EQUITY: { start: 6000, end: 6999 },
        PL_REVENUE: { start: 7000, end: 7999 },
        PL_EXPENSES: { start: 8000, end: 9999 }
      };
      
      Object.entries(defaultRanges).forEach(([key, range]) => {
        this.dynamicRanges.set(key, range);
      });
    }
    
    // Initialize counters
    this.dynamicRanges.forEach((range, key) => {
      this.accountNumberCounters.set(key, range.start);
    });
    
    console.log('🎯 Dynamic ranges allocated:', Object.fromEntries(this.dynamicRanges));
  }
  
  // Helper: Extract account numbers from trial balance
  private extractTrialBalanceAccountNumbers(file: FileWithPreview): number[] {
    if (!file.fullData) return [];
    
    const [headers, ...rows] = file.fullData;
    const accountNumbers: number[] = [];
    
    rows.forEach(row => {
      const accountNum = row[0]?.toString().trim();
      if (accountNum) {
        const parsed = parseInt(accountNum);
        if (!isNaN(parsed)) {
          accountNumbers.push(parsed);
        }
      }
    });
    
    return accountNumbers;
  }
  
  // Process files in smart priority order (type priority + date recency)
  determineMasterFile(files: FileWithPreview[]): FileWithPreview[] {
    console.log('🎯 Determining file processing order with date-based recency...');
    
    // Group files by type with mutually exclusive categorization
    // Priority order: Trial Balance > P&L > Balance Sheet
    const trialBalance = files.filter(f => getEffectiveFormat(f)?.includes('Trial Balance'));
    const remainingFiles = files.filter(f => !getEffectiveFormat(f)?.includes('Trial Balance'));
    const pnlFiles = remainingFiles.filter(f => getEffectiveFormat(f)?.includes('P&L'));
    const balanceSheets = remainingFiles.filter(f => !getEffectiveFormat(f)?.includes('P&L') && getEffectiveFormat(f)?.includes('Balance Sheet'));
    
    // Sort each group by most recent date (most recent first)
    const sortByRecency = (filesGroup: FileWithPreview[]) => {
      return filesGroup.sort((a, b) => {
        // Files with dates take priority
        if (a.mostRecentDate && !b.mostRecentDate) return -1;
        if (!a.mostRecentDate && b.mostRecentDate) return 1;
        
        // If both have dates, most recent first
        if (a.mostRecentDate && b.mostRecentDate) {
          return b.mostRecentDate.getTime() - a.mostRecentDate.getTime();
        }
        
        // If neither has dates, maintain original order
        return 0;
      });
    };
    
    const sortedTrialBalance = sortByRecency([...trialBalance]);
    const sortedBalanceSheets = sortByRecency([...balanceSheets]);
    const sortedPnlFiles = sortByRecency([...pnlFiles]);
    
    // Log the ordering decision
    const logFileOrder = (fileGroup: FileWithPreview[], groupName: string) => {
      if (fileGroup.length > 0) {
        console.log(`📋 ${groupName} processing order:`);
        fileGroup.forEach((file, index) => {
          const dateStr = file.mostRecentDate 
            ? file.mostRecentDate.toLocaleDateString() 
            : 'No date found';
          console.log(`   ${index + 1}. ${file.file.name} (${dateStr})`);
        });
      }
    };
    
    logFileOrder(sortedTrialBalance, 'Trial Balance');
    logFileOrder(sortedBalanceSheets, 'Balance Sheet');
    logFileOrder(sortedPnlFiles, 'P&L');
    
    // Log the final processing order to help debug duplicates
    const finalOrder = [
      ...sortedTrialBalance,
      ...sortedBalanceSheets,
      ...sortedPnlFiles
    ].filter(Boolean);
    
    console.log(`📊 Final processing order (${finalOrder.length} files):`);
    finalOrder.forEach((file, index) => {
      console.log(`   ${index + 1}. ${file.file.name} (${file.detectedFormat})`);
    });
    
    // Return in priority order: Trial Balance → Balance Sheets → P&L
    // Within each type, most recent files are processed first
    return [
      ...sortedTrialBalance,
      ...sortedBalanceSheets,
      ...sortedPnlFiles
    ].filter(Boolean);
  }
  
  // Smart duplicate resolution
  generateUniqueAccountKey(accountName: string, context: string, fileType: string): string {
    const baseKey = accountName.trim();
    const normalizedBaseKey = this.normalizeAccountNameForMatching(baseKey);
    const existingKey = this.canonicalAccountNames.get(normalizedBaseKey);
      
    if (!existingKey) {
      return accountName;
    }
    
    switch (this.config.duplicateResolution) {
      case 'merge':
        return existingKey;
      case 'suffix_context':
        const suffix = fileType.includes('Balance Sheet') ? 'BS' : 
                      fileType.includes('P&L') ? 'PL' : 'TB';
        return `${accountName} (${suffix})`;
      default:
        return accountName;
    }
  }
  
  // Smart account number assignment
  assignAccountNumber(accountKey: string, fileType: string, context: string, originalAccountNumber?: string): string {
    const normalizedAccountKey = this.normalizeAccountNameForMatching(accountKey);

    const existingAccountNumber = this.accountKeyToNumber.get(normalizedAccountKey);
    if (existingAccountNumber) {
      return existingAccountNumber;
    }

    // For trial balance, preserve original
    if (fileType.includes('Trial Balance') && originalAccountNumber) {
      if (this.preservedTrialBalanceAccounts.has(originalAccountNumber)) {
        console.log(`🔒 Preserving TB account: ${originalAccountNumber} → ${accountKey}`);
        this.registerAssignedAccount(accountKey, originalAccountNumber);
        return originalAccountNumber;
      }
    }
    
    // For synthetic accounts
    const rangeKey = this.determineRangeKey(fileType, context);
    const range = this.dynamicRanges.get(rangeKey);
    
    if (!range) {
      console.warn(`No range found for ${rangeKey}, using fallback`);
      const fallbackNumber = this.getNextFallbackNumber();
      this.registerAssignedAccount(accountKey, fallbackNumber);
      return fallbackNumber;
    }
    
    let currentCounter = this.accountNumberCounters.get(rangeKey) || range.start;
    
    while (currentCounter <= range.end) {
      const candidateNumber = currentCounter.toString();
      if (!this.usedAccountNumbers.has(candidateNumber)) {
        this.accountNumberCounters.set(rangeKey, currentCounter + 1);
        console.log(`✅ Assigned synthetic account: ${currentCounter} → ${accountKey} (${rangeKey})`);
        this.registerAssignedAccount(accountKey, candidateNumber);
        return candidateNumber;
      }
      currentCounter++;
    }
    
    const fallbackNumber = this.getNextFallbackNumber();
    console.warn(`⚠️  Range exhausted, using fallback: ${fallbackNumber} → ${accountKey}`);
    this.registerAssignedAccount(accountKey, fallbackNumber);
    return fallbackNumber.toString();
  }
  
  private determineRangeKey(fileType: string, context: string): string {
    if (fileType.includes('Trial Balance')) return 'TRIAL_BALANCE';
    
    if (fileType.includes('Balance Sheet')) {
      if (context.includes('asset')) return 'BALANCE_SHEET_ASSETS';
      if (context.includes('liability')) return 'BALANCE_SHEET_LIABILITIES';
      if (context.includes('equity')) return 'BALANCE_SHEET_EQUITY';
      return 'BALANCE_SHEET_ASSETS';
    }
    
    if (fileType.includes('P&L')) {
      return context.includes('revenue') ? 'PL_REVENUE' : 'PL_EXPENSES';
    }
    
    return 'PL_EXPENSES';
  }

  // Extract most recent date from file data (works for both horizontal and vertical formats)
  extractMostRecentDate(file: FileWithPreview): Date | undefined {
    if (!file.fullData || file.fullData.length < 2) return undefined;
    
    const [headers, ...rows] = file.fullData;
    let mostRecentDate: Date | undefined = undefined;
    
    console.log(`📅 Extracting dates from ${file.file.name} (${file.detectedFormat})`);
    
    // Horizontal format: Check headers for date columns
    if (getEffectiveFormat(file)?.includes('Horizontal')) {
      headers.forEach((header) => {
        if (!header || header.trim() === '') return;
        
        const date = this.parseDate(header);
        if (date && (!mostRecentDate || date > mostRecentDate)) {
          mostRecentDate = date;
          console.log(`📅 Found horizontal date: ${header} → ${date.toLocaleDateString()}`);
        }
      });
    }
    
    // Vertical format: Look for date/period column in data rows
    if (getEffectiveFormat(file)?.includes('Vertical') || getEffectiveFormat(file)?.includes('Trial Balance')) {
      const lowerHeaders = headers.map(h => h.toLowerCase());
      const dateColumnIndex = lowerHeaders.findIndex(h => 
        h.includes('date') || h.includes('period') || h.includes('month')
      );
      
      if (dateColumnIndex >= 0) {
        rows.forEach(row => {
          const dateValue = row[dateColumnIndex];
          if (dateValue) {
            const date = this.parseDate(dateValue);
            if (date && (!mostRecentDate || date > mostRecentDate)) {
              mostRecentDate = date;
              console.log(`📅 Found vertical date: ${dateValue} → ${date.toLocaleDateString()}`);
            }
          }
        });
      }
    }
    
    // Fallback: Parse all data cells looking for dates
    if (!mostRecentDate) {
      rows.forEach(row => {
        row.forEach(cell => {
          if (cell && typeof cell === 'string') {
            const date = this.parseDate(cell);
            if (date && (!mostRecentDate || date > mostRecentDate)) {
              mostRecentDate = date;
              console.log(`📅 Found fallback date: ${cell} → ${date.toLocaleDateString()}`);
            }
          }
        });
      });
    }
    
    if (mostRecentDate) {
      console.log(`🎯 Most recent date in ${file.file.name}: ${(mostRecentDate as Date).toLocaleDateString()}`);
    } else {
      console.log(`⚠️  No valid dates found in ${file.file.name}`);
    }
    
    return mostRecentDate;
  }
  
  // Enhanced date parsing to handle various financial date formats
  private parseDate(dateStr: string): Date | undefined {
    if (!dateStr || typeof dateStr !== 'string') return undefined;
    
    const trimmed = dateStr.toString().trim();
    if (!trimmed) return undefined;
    
    // Common financial date patterns
    const patterns = [
      // "MMM-YY" format (Jan-24)
      /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[-\s](\d{2})$/i,
      // "MM/DD/YYYY" format
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
      // "YYYY-MM-DD" format
      /^\d{4}-\d{1,2}-\d{1,2}$/,
      // "DD-MMM-YYYY" format
      /^\d{1,2}[-\s](jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[-\s]\d{2,4}$/i,
      // Full month names
      /^(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2},?\s+\d{2,4}$/i
    ];
    
    // Check if it matches common patterns first
    const matchesPattern = patterns.some(pattern => pattern.test(trimmed));
    
    if (matchesPattern || !isNaN(Date.parse(trimmed))) {
      try {
        // Handle "MMM-YY" format specially
        const mmyy = trimmed.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[-\s](\d{2})$/i);
        if (mmyy) {
          const [, month, year] = mmyy;
          const fullYear = parseInt(year) + (parseInt(year) > 50 ? 1900 : 2000);
          const date = new Date(`${month} 1, ${fullYear}`);
          // Return end of month for better comparison
          return new Date(date.getFullYear(), date.getMonth() + 1, 0);
        }
        
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime()) && parsed.getFullYear() > 1900) {
          return parsed;
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
    
    return undefined;
  }

  // Enhanced detectPeriodType method with confidence scoring and date gap analysis
  detectPeriodType(headers: string[], rows: string[][]): {
    periodType: 'monthly' | 'quarterly' | 'annual';
    confidence: number;
    detectedPeriods: string[];
    periodPattern: string;
    reasons: string[];
    warnings: string[];
  } {
    const allTextData = [...headers, ...rows.flat()].join(' ').toLowerCase();
    const detectedPeriods: string[] = [];
    const reasons: string[] = [];
    const warnings: string[] = [];
    
    // STEP 1: Parse all dates from headers to analyze gaps
    const parsedDates: Date[] = [];
    headers.forEach(header => {
      const date = this.parseDate(header);
      if (date) {
        parsedDates.push(date);
      }
    });
    
    // Sort dates for gap analysis
    parsedDates.sort((a, b) => a.getTime() - b.getTime());
    
    // STEP 2: Analyze date gaps if we have multiple dates
    let gapAnalysisResult: 'monthly' | 'quarterly' | 'annual' | null = null;
    if (parsedDates.length >= 2) {
      const gaps = [];
      for (let i = 1; i < parsedDates.length; i++) {
        const gap = parsedDates[i].getTime() - parsedDates[i-1].getTime();
        const gapInDays = gap / (1000 * 60 * 60 * 24);
        gaps.push(gapInDays);
      }
      
      const avgGap = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
      
      // Classify based on average gap
      if (avgGap >= 300 && avgGap <= 400) { // ~11-13 months (annual)
        gapAnalysisResult = 'annual';
        reasons.push(`Date gap analysis: ~${Math.round(avgGap)} days suggests annual periods`);
      } else if (avgGap >= 80 && avgGap <= 100) { // ~3 months (quarterly)
        gapAnalysisResult = 'quarterly';
        reasons.push(`Date gap analysis: ~${Math.round(avgGap)} days suggests quarterly periods`);
      } else if (avgGap >= 25 && avgGap <= 35) { // ~1 month (monthly)
        gapAnalysisResult = 'monthly';
        reasons.push(`Date gap analysis: ~${Math.round(avgGap)} days suggests monthly periods`);
      } else {
        reasons.push(`Date gap analysis: ~${Math.round(avgGap)} days - unclear pattern`);
      }
    }
    
    // STEP 3: Pattern matching (existing logic)
    const quarterlyPatterns = [
      { pattern: /q[1-4][-\s]*20\d{2}/gi, name: 'Quarterly Format (Q1-2024)' },
      { pattern: /quarter/gi, name: 'Quarter keyword' },
      { pattern: /qtd/gi, name: 'Quarter-to-date' },
      { pattern: /(jan|apr|jul|oct)[-\s]*(mar|jun|sep|dec)/gi, name: 'Quarter ranges' }
    ];
    
    const annualPatterns = [
      { pattern: /\b20\d{2}\b/g, name: 'Year format (2024)' },
      { pattern: /fy\s*20\d{2}/gi, name: 'Fiscal Year' },
      { pattern: /year\s*end/gi, name: 'Year end' },
      { pattern: /annual/gi, name: 'Annual keyword' },
      { pattern: /ytd/gi, name: 'Year-to-date' },
      { pattern: /12\/31\/\d{2,4}/gi, name: 'Year-end dates (12/31)' } // NEW: Year-end pattern
    ];
    
    // Enhanced monthly patterns - exclude year-end dates
    const monthlyPatterns = [
      { pattern: /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[-\s]*\d{2}/gi, name: 'Month-Year format' },
      { pattern: /month/gi, name: 'Month keyword' }
      // Removed generic date pattern to prevent false positives with annual year-end dates
    ];
    
    // Count pattern matches
    let quarterlyMatches = 0;
    let annualMatches = 0;
    let monthlyMatches = 0;
    
    quarterlyPatterns.forEach(({ pattern, name }) => {
      const matches = allTextData.match(pattern);
      if (matches) {
        quarterlyMatches += matches.length;
        detectedPeriods.push(...matches);
        reasons.push(`Found ${matches.length} ${name} indicators`);
      }
    });
    
    annualPatterns.forEach(({ pattern, name }) => {
      const matches = allTextData.match(pattern);
      if (matches) {
        annualMatches += matches.length;
        detectedPeriods.push(...matches);
        reasons.push(`Found ${matches.length} ${name} indicators`);
      }
    });
    
    monthlyPatterns.forEach(({ pattern, name }) => {
      const matches = allTextData.match(pattern);
      if (matches) {
        monthlyMatches += matches.length;
        detectedPeriods.push(...matches);
        reasons.push(`Found ${matches.length} ${name} indicators`);
      }
    });
    
    // STEP 4: Combine gap analysis with pattern matching
    let periodType: 'monthly' | 'quarterly' | 'annual';
    let confidence: number;
    let periodPattern: string = '';
    
    // Gap analysis takes priority if available and conclusive
    if (gapAnalysisResult) {
      periodType = gapAnalysisResult;
      confidence = 85; // High confidence from date gap analysis
      periodPattern = `${gapAnalysisResult} (gap analysis)`;
      reasons.push(`Date gap analysis provides strong ${gapAnalysisResult} indication`);
      
      // Boost confidence if pattern matching agrees
      if ((gapAnalysisResult === 'quarterly' && quarterlyMatches > 0) ||
          (gapAnalysisResult === 'annual' && annualMatches > 0) ||
          (gapAnalysisResult === 'monthly' && monthlyMatches > 0)) {
        confidence = 95;
        reasons.push('Gap analysis confirmed by pattern matching');
      }
    } else {
      // Fall back to pattern matching
      const totalMatches = quarterlyMatches + annualMatches + monthlyMatches;
      
      if (totalMatches === 0) {
        periodType = 'monthly';
        confidence = 40;
        reasons.push('Insufficient period indicators - defaulting to monthly');
        warnings.push('Could not reliably detect period type - assuming monthly data');
      } else if (quarterlyMatches > annualMatches && quarterlyMatches > monthlyMatches) {
        periodType = 'quarterly';
        confidence = Math.min(95, 60 + (quarterlyMatches / totalMatches) * 35);
        periodPattern = detectedPeriods.find(p => /q[1-4]/i.test(p)) || 'quarterly';
        reasons.push('Quarterly indicators dominate');
      } else if (annualMatches > monthlyMatches && annualMatches > quarterlyMatches) {
        periodType = 'annual';
        confidence = Math.min(95, 60 + (annualMatches / totalMatches) * 35);
        periodPattern = detectedPeriods.find(p => /20\d{2}/.test(p)) || 'annual';
        reasons.push('Annual indicators dominate');
      } else if (monthlyMatches > 0) {
        periodType = 'monthly';
        confidence = Math.min(95, 60 + (monthlyMatches / totalMatches) * 35);
        periodPattern = detectedPeriods.find(p => /(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(p)) || 'monthly';
        reasons.push('Monthly indicators dominate');
      } else {
        periodType = 'monthly';
        confidence = 50;
        reasons.push('Mixed period indicators detected - defaulting to monthly');
        warnings.push('File contains mixed period references - please verify data type');
      }
    }
    
    // Additional confidence adjustments
    if (quarterlyMatches > 0 && monthlyMatches > 0) {
      confidence = Math.max(40, confidence - 20);
      warnings.push('Mixed quarterly and monthly indicators detected');
    }
    
    if (annualMatches > 0 && (quarterlyMatches > 0 || monthlyMatches > 0)) {
      confidence = Math.max(40, confidence - 15);
      warnings.push('Mixed annual and sub-annual indicators detected');
    }
    
    return {
      periodType,
      confidence: Math.round(confidence),
      detectedPeriods: [...new Set(detectedPeriods)],
      periodPattern,
      reasons,
      warnings
    };
  }

  // Add this method inside the SmartAccountConsolidationEngine class
  getStats() {
    return {
      preservedAccountsCount: this.preservedTrialBalanceAccounts.size,
      syntheticAccountsCount: this.accountKeyToNumber.size,
      rangesUsed: Object.fromEntries(this.dynamicRanges)
    };
  }
}

type FinancialFileType = 'trial_balance' | 'profit_loss' | 'balance_sheet';

interface FileWithPreview {
  file: File;
  id: string;
  preview: string[][] | null;
  fullData: string[][] | null;
  detectedFormat: string | null;
  isValid: boolean;
  errors: string[];
  rowCount: number;
  columnCount: number;
  mostRecentDate?: Date; // Add most recent date found in file data
  periodType?: 'monthly' | 'quarterly' | 'annual'; // NEW: detected period type
  periodDetectionConfidence?: number; // NEW: confidence score 0-100
  detectedPeriods?: string[]; // NEW: actual periods found
  periodPattern?: string; // NEW: detected pattern (e.g., "Q1-2024", "Dec-24", "2024")
  userSelectedType: FinancialFileType | null; // User-confirmed file type
  formatConfidence: number; // 0-100 confidence in auto-detection
}

// Maps user-selected file type to a format string compatible with the existing pipeline
// Preserves layout suffix (Horizontal/Vertical) from original auto-detection
function getEffectiveFormat(file: FileWithPreview): string | null {
  if (!file.userSelectedType) {
    return file.detectedFormat;
  }

  // Extract layout suffix from detectedFormat if available
  const layoutMatch = file.detectedFormat?.match(/\(([^)]+)\)/);
  // Default layout when none detected
  const layoutSuffix = layoutMatch
    ? ` (${layoutMatch[1]})`
    : ' (Horizontal - Period Columns)';

  switch (file.userSelectedType) {
    case 'trial_balance':
      return `Trial Balance${layoutSuffix}`;
    case 'profit_loss':
      return `P&L Statement${layoutSuffix}`;
    case 'balance_sheet':
      return `Balance Sheet${layoutSuffix}`;
    default:
      return file.detectedFormat;
  }
}

interface FileUploadProps {
  onDataLoaded: (data: AccountEntry[], detectedPeriodType?: 'monthly' | 'quarterly' | 'annual') => void;
  onDataProcessed?: (data: AccountEntry[], isAlreadyMerged?: boolean, detectedPeriodType?: 'monthly' | 'quarterly' | 'annual') => void;
  selectedProject?: { name: string } | null;
  mode?: 'initial' | 'additional';
  existingData?: AccountEntry[];
  isAdditionalData?: boolean;
  onModalClose?: () => void;
}

// Progress Stepper Component - Updated to handle additional data mode
const ProgressStepper: React.FC<{ currentStep: number; mode: 'initial' | 'additional' }> = ({ currentStep, mode }) => {
  const initialSteps = [
    { id: 1, name: 'Upload Files', description: 'Add your financial data files' },
    { id: 2, name: 'Process Data', description: 'Format detection and validation' },
    { id: 3, name: 'Success', description: 'Upload completed' }
  ];
  
  const additionalSteps = [
    { id: 1, name: 'Upload Files', description: 'Add additional financial data' },
    { id: 2, name: 'Process Data', description: 'Format detection and validation' },
    { id: 3, name: 'Review Conflicts', description: 'Resolve data conflicts' },
    { id: 4, name: 'Success', description: 'Upload completed' }
  ];
  
  const steps = mode === 'additional' ? additionalSteps : initialSteps;

  return (
    <div className="mb-8 px-4">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center relative">
              <div
                className={`
                  flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm font-semibold
                  ${currentStep === step.id
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'bg-gray-100 border-gray-300 text-gray-400'
                  }
                `}
              >
                {step.id}
              </div>
              <div className="mt-2 text-center">
                <div className={`text-sm font-medium ${currentStep === step.id ? 'text-blue-600' : 'text-gray-500'}`}>
                  {step.name}
                </div>
                <div className="text-xs text-gray-400 max-w-24">
                  {step.description}
                </div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`
                  flex-1 h-0.5 mx-4 -mt-8 bg-gray-300
                `}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Preview Modal Component
const PreviewModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  fileData: FileWithPreview | null;
}> = ({ isOpen, onClose, fileData }) => {
  if (!isOpen || !fileData || !fileData.preview) return null;

  const previewData = fileData.preview.slice(0, 6); // Header + 5 rows
  const [headers, ...rows] = previewData;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-6xl max-h-[80vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{fileData.file.name}</h3>
            <p className="text-sm text-gray-500">
              Preview (first 5 of {fileData.rowCount} rows)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-96">
          <div className="overflow-x-auto border border-gray-200 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {headers?.map((header, index) => (
                    <th 
                      key={index}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-r border-gray-200 last:border-r-0"
                    >
                      {header || `Column ${index + 1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {row.map((cell, cellIndex) => (
                      <td 
                        key={cellIndex}
                        className="px-4 py-3 whitespace-nowrap border-r border-gray-200 last:border-r-0 text-gray-900"
                      >
                        {cell || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export function FileUpload({ onDataLoaded, onDataProcessed, selectedProject, mode: propMode = 'initial', existingData = [], isAdditionalData = false, onModalClose }: FileUploadProps) {
  // Determine the actual mode based on isAdditionalData prop
  const mode = isAdditionalData ? 'additional' : propMode;
  
  // Existing state
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [debugMode] = useState(true); // Keep debug logging for console auditing
  const [previewModal, setPreviewModal] = useState<{ isOpen: boolean; fileData: FileWithPreview | null }>({
    isOpen: false,
    fileData: null
  });
  const [showFileFormatsHelp, setShowFileFormatsHelp] = useState(false);
  const [selectedFileType, setSelectedFileType] = useState<string | null>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  // New state for step management
  const [currentStep, setCurrentStep] = useState<'upload' | 'processing' | 'conflicts' | 'success'>('upload');
  const [processedData, setProcessedData] = useState<any>(null);
  const [conflictAnalysis, setConflictAnalysis] = useState<any>(null);
  const [detectedPeriodType, setDetectedPeriodType] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showHelpModal) {
          setShowHelpModal(false);
        } else if (selectedFileType) {
          setSelectedFileType(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [selectedFileType, showHelpModal]);

  // Helper function to get step number
  const getStepNumber = () => {
    if (mode === 'additional') {
      switch (currentStep) {
        case 'upload': return 1;
        case 'processing': return 2;
        case 'conflicts': return 3;
        case 'success': return 4;
        default: return 1;
      }
    } else {
      switch (currentStep) {
        case 'upload': return 1;
        case 'processing': return 2;
        case 'success': return 3;
        default: return 1;
      }
    }
  };

  // Debug logging
  const debugLog = useCallback((message: string, data?: any) => {
    if (debugMode) {
      console.log(`[FileUpload Debug] ${message}`, data);
    }
  }, [debugMode]);

  // Multi-signal account identifier detection (matching API logic)
  const hasAccountIdentifier = useCallback((lowerHeaders: string[], rows: string[][]): boolean => {
    // Strategy 1: Header-based detection with comprehensive synonyms
    const ACCOUNT_IDENTIFIER_SYNONYMS = [
      ['account', 'number'],
      ['account', 'no'],
      ['account', '#'],
      ['account', 'code'],    // NEW: Handle "Account Code"
      ['acct', 'no'],
      ['acct', 'code'],
      ['acc', 'no'],
      ['acc', 'code'],
      ['account', 'id']
    ];
    
    const hasHeaderMatch = ACCOUNT_IDENTIFIER_SYNONYMS.some(([word1, word2]) =>
      lowerHeaders.some(header => 
        header.includes(word1) && header.includes(word2)
      )
    );
    
    if (hasHeaderMatch) {
      debugLog('Account identifier found via header analysis');
      return true;
    }
    
    // Strategy 2: Flexible structural analysis - check columns before date boundary for account-like codes
    if (rows.length === 0) return false;
    
    // First, find where date columns start to establish search boundary
    const dateStartIndex = findDateColumnBoundary(lowerHeaders);
    const searchBoundary = dateStartIndex > 0 ? dateStartIndex : Math.min(4, lowerHeaders.length); // Search up to 4 columns if no dates found
    
    debugLog(`Searching for account identifiers in columns 0-${searchBoundary - 1}, date boundary at: ${dateStartIndex}`);
    
    // Check each column before the date boundary
    for (let colIndex = 0; colIndex < searchBoundary; colIndex++) {
      const columnValues = rows.slice(0, Math.min(10, rows.length))
        .map(row => row[colIndex])
        .filter(val => val && val.toString().trim());
      
      if (columnValues.length === 0) continue;
      
      // IMPROVED: More strict account code patterns to avoid false positives
      // Check for numeric account codes (4-5 digits, typical accounting systems)
      const numericAccountPattern = /^\d{4,5}$/;
      const numericMatches = columnValues.filter(val => 
        numericAccountPattern.test(val.toString().trim())
      ).length;
      
      // Check for alphanumeric account codes (like "1000", "A100", etc.) - more strict
      const alphanumericAccountPattern = /^[A-Z]{1,2}\d{3,4}$/i; // Letter(s) followed by 3-4 digits
      const alphanumericMatches = columnValues.filter(val => 
        alphanumericAccountPattern.test(val.toString().trim())
      ).length;
      
      // IMPROVED: Higher threshold and require more data points
      const structuralThreshold = Math.ceil(columnValues.length * 0.8); // 80% threshold (was 70%)
      const minDataPoints = 3; // Require at least 3 data points
      
      if (columnValues.length >= minDataPoints && numericMatches >= structuralThreshold) {
        debugLog(`Account identifier found in column ${colIndex} via numeric structure analysis: ${numericMatches}/${columnValues.length} matches`);
        return true;
      }
      
      if (columnValues.length >= minDataPoints && alphanumericMatches >= structuralThreshold) {
        debugLog(`Account identifier found in column ${colIndex} via alphanumeric structure analysis: ${alphanumericMatches}/${columnValues.length} matches`);
        return true;
      }
    }
    
    // Strategy 3: Content-based detection - look for trial balance mathematical properties
    const hasTrialBalanceStructure = checkTrialBalanceStructure(rows);
    if (hasTrialBalanceStructure) {
      debugLog('Account identifier inferred from trial balance mathematical structure');
      return true;
    }
    
    debugLog('No account identifier detected via any method');
    return false;
  }, [debugLog]);

  // Find where date columns start to establish search boundary for account identifiers
  const findDateColumnBoundary = useCallback((lowerHeaders: string[]): number => {
    // Look for the first column that appears to be a date
    for (let i = 0; i < lowerHeaders.length; i++) {
      const header = lowerHeaders[i];
      if (!header || header.trim() === '') continue;
      
      // Check for date-related headers
      const dateKeywords = ['date', 'month', 'period', 'year'];
      const hasDateKeyword = dateKeywords.some(keyword => header.includes(keyword));
      
      if (hasDateKeyword) {
        debugLog(`Found date keyword boundary at column ${i}: "${header}"`);
        return i;
      }
      
      // Check for date patterns in header text
      const datePatterns = [
        /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,        // MM/DD/YYYY
        /^\d{4}-\d{1,2}-\d{1,2}$/,           // YYYY-MM-DD
        /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // Month names
        /^\w{3}[-\s]\d{2,4}$/,               // Jan-24, Feb 2024
        /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/, // Various date separators
      ];
      
      const matchesDatePattern = datePatterns.some(pattern => pattern.test(header.trim()));
      if (matchesDatePattern || !isNaN(Date.parse(header))) {
        debugLog(`Found date pattern boundary at column ${i}: "${header}"`);
        return i;
      }
    }
    
    // No date columns found
    debugLog('No date column boundary found');
    return -1;
  }, [debugLog]);

  // Check for trial balance mathematical properties
  const checkTrialBalanceStructure = useCallback((rows: string[][]): boolean => {
    if (rows.length < 5) return false; // Need minimum rows for analysis
    
    // Look for diverse account types (assets, liabilities, equity, revenue, expenses)
    const accountTypeIndicators = {
      assets: ['cash', 'bank', 'receivable', 'inventory', 'prepaid', 'equipment', 'building'],
      liabilities: ['payable', 'loan', 'debt', 'accrued', 'liability'],
      equity: ['equity', 'capital', 'retained', 'stock', 'earnings'],
      revenue: ['revenue', 'sales', 'income', 'fees'],
      expenses: ['expense', 'cost', 'rent', 'utilities', 'salaries', 'wages', 'supplies']
    };
    
    const foundTypes = new Set<string>();
    const allText = rows.flat().join(' ').toLowerCase();
    
    Object.entries(accountTypeIndicators).forEach(([type, keywords]) => {
      if (keywords.some(keyword => allText.includes(keyword))) {
        foundTypes.add(type);
      }
    });
    
    // Trial balances should have at least 3 different account types
    const hasMinimumDiversity = foundTypes.size >= 3;
    
    // Check for numerical balance (sum should be close to zero for trial balance)
    const hasNumericalBalance = checkNumericalBalance(rows);
    
    debugLog(`Trial balance structure analysis: types=${foundTypes.size}, balance=${hasNumericalBalance}`);
    
    return hasMinimumDiversity && hasNumericalBalance;
  }, [debugLog]);

  // Check if the numerical data has trial balance balancing properties
  const checkNumericalBalance = useCallback((rows: string[][]): boolean => {
    if (rows.length === 0) return false;
    
    // Find numerical columns (skip first 2 which are typically account info)
    const numericalColumns: number[] = [];
    
    for (let colIndex = 2; colIndex < (rows[0]?.length || 0); colIndex++) {
      const columnValues = rows.slice(0, Math.min(5, rows.length))
        .map(row => row[colIndex])
        .filter(val => val && val.toString().trim());
      
      const numericalCount = columnValues.filter(val => {
        const cleanVal = val.toString().replace(/[$,\s()]/g, '');
        return !isNaN(parseFloat(cleanVal));
      }).length;
      
      if (numericalCount >= Math.ceil(columnValues.length * 0.8)) {
        numericalColumns.push(colIndex);
      }
    }
    
    if (numericalColumns.length === 0) return false;
    
    // For each numerical column, check if values have appropriate mix of positive/negative
    // (Trial balances typically have both debits and credits)
    for (const colIndex of numericalColumns) {
      const values = rows.map(row => {
        const val = row[colIndex];
        if (!val || val.toString().trim() === '') return 0;
        
        let cleanVal = val.toString().replace(/[$,\s]/g, '');
        let isNegative = false;
        
        if (cleanVal.startsWith('(') && cleanVal.endsWith(')')) {
          isNegative = true;
          cleanVal = cleanVal.slice(1, -1);
        }
        
        const numVal = parseFloat(cleanVal);
        return isNaN(numVal) ? 0 : (isNegative ? -numVal : numVal);
      }).filter(val => Math.abs(val) > 0.01);
      
      if (values.length < 3) continue;
      
      const positiveCount = values.filter(val => val > 0).length;
      const negativeCount = values.filter(val => val < 0).length;
      
      // Trial balances should have both positive and negative values
      // (representing debits and credits or different account types)
      const hasBalanceMix = positiveCount > 0 && negativeCount > 0;
      const balanceRatio = Math.min(positiveCount, negativeCount) / Math.max(positiveCount, negativeCount);
      
      if (hasBalanceMix && balanceRatio > 0.2) { // At least 20% mix
        return true;
      }
    }
    
    return false;
  }, []);

  // Find the actual header row for P&L files
  const findActualHeaderRow = useCallback((data: string[][]): { headerRow: string[]; dataRows: string[][]; headerRowIndex: number } => {
    // Look for rows that contain date patterns
    for (let i = 0; i < Math.min(data.length, 10); i++) {
      const row = data[i];
      const dateColumnCount = row.filter((cell, index) => {
        if (!cell || index < 2) return false; // Skip first 2 columns
        const datePatterns = [
          /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
          /^\d{4}-\d{1,2}-\d{1,2}$/,
          /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
        ];
        return datePatterns.some(pattern => pattern.test(cell.trim())) || !isNaN(Date.parse(cell));
      }).length;
      
      // If we find a row with 3+ date columns, this is likely the header
      if (dateColumnCount >= 3) {
        debugLog(`Found actual header row at index ${i} with ${dateColumnCount} date columns:`, row);
        return {
          headerRow: row,
          dataRows: data.slice(i + 1),
          headerRowIndex: i
        };
      }
    }
    
    // Fallback to first row
    debugLog('Using first row as header (no date headers found)');
    return {
      headerRow: data[0] || [],
      dataRows: data.slice(1),
      headerRowIndex: 0
    };
  }, []);

  // Auto-detect file format
  const detectFormat = useCallback((headers: string[], rows: string[][]): string => {
    debugLog('Detecting format for headers:', headers);
    
    // First, clean up empty columns from headers to avoid false positives
    const cleanedHeaders = headers.filter((h, index) => {
      // Keep non-empty headers
      if (h && h.trim() !== '') return true;
      
      // For empty headers, check if they're between date columns
      // If there are date columns after this position, it might be a spacer
      const hasDateAfter = headers.slice(index + 1).some(h => {
        if (!h || h.trim() === '') return false;
        const datePatterns = [
          /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
          /^\d{4}-\d{1,2}-\d{1,2}$/,
          /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
          /^\w{3}[-\s]\d{2}$/,
          /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/,
        ];
        return datePatterns.some(pattern => pattern.test(h.trim())) || !isNaN(Date.parse(h.trim()));
      });
      
      // Keep empty columns that are likely spacers between date columns
      return hasDateAfter;
    });
    
    debugLog('Cleaned headers:', cleanedHeaders);
    debugLog('Original headers length:', headers.length, 'Cleaned headers length:', cleanedHeaders.length);
    
    const lowerHeaders = cleanedHeaders.map(h => h.toLowerCase());
    
    // Enhanced account identifier detection matching API logic
    const hasAccountNumber = hasAccountIdentifier(lowerHeaders, rows);
    debugLog('Account identifier detection result:', hasAccountNumber);
    
    const hasAccountName = lowerHeaders.some(h => 
      h.includes('account') && (h.includes('name') || h.includes('description'))
    );
    
    const hasDebit = lowerHeaders.some(h => h.includes('debit'));
    const hasCredit = lowerHeaders.some(h => h.includes('credit'));
    
    // Check for a date/period COLUMN (vertical format) vs multiple date COLUMNS (horizontal format)
    const hasDateColumn = lowerHeaders.some(h => 
      h.includes('month') || h.includes('date') || h.includes('period')
    );
    
    // FIXED: Check for actual date values in cleaned headers (horizontal format)
    // For P&L files: Skip empty column and description column
    const dateColumns = cleanedHeaders.filter((h, index) => {
      if (!h || h.trim() === '') return false;
      
      // For files with account numbers, skip first 2 columns (account number & name)
      // For files without account numbers, skip first 2 columns (empty column 0 & description column 1)
      const skipColumns = hasAccountNumber ? 2 : 2;
      if (index < skipColumns) return false;
      
      const datePatterns = [
        /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,        // MM/DD/YYYY
        /^\d{4}-\d{1,2}-\d{1,2}$/,           // YYYY-MM-DD  
        /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i, // Month names
        /^\w{3}[-\s]\d{2}$/,                 // Jan-24, Feb 24
        /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/, // Various date separators
      ];
      
      const trimmedHeader = h.trim();
      return datePatterns.some(pattern => pattern.test(trimmedHeader)) || 
             !isNaN(Date.parse(trimmedHeader));
    });
    
    const hasDateColumns = dateColumns.length >= 1; // Even 1 date column suggests horizontal
    
    debugLog('Format detection details:');
    debugLog('- hasAccountNumber:', hasAccountNumber);
    debugLog('- hasAccountName:', hasAccountName);
    debugLog('- hasDebit:', hasDebit);
    debugLog('- hasCredit:', hasCredit);
    debugLog('- hasDateColumn:', hasDateColumn);
    debugLog('- dateColumns.length:', dateColumns.length);
    debugLog('- dateColumns:', dateColumns);
    debugLog('- hasDateColumns:', hasDateColumns);
    
    // FIXED: Prioritize horizontal detection when date columns are found
    if (hasAccountNumber && hasAccountName && hasDateColumns) {
      debugLog('→ Detected as: Trial Balance (Horizontal - Period Columns)');
      return 'Trial Balance (Horizontal - Period Columns)';
    } else if (hasAccountNumber && hasAccountName && hasDebit && hasCredit && !hasDateColumn) {
      debugLog('→ Detected as: Trial Balance (Vertical - Debit/Credit)');
      return 'Trial Balance (Vertical - Debit/Credit)';
    } else if (hasAccountNumber && hasAccountName && hasDateColumn && hasDebit && hasCredit) {
      debugLog('→ Detected as: Trial Balance (Vertical - Period Data)');
      return 'Trial Balance (Vertical - Period Data)';
    } else if (hasAccountNumber && hasAccountName && hasDateColumn && !hasDebit && !hasCredit) {
      debugLog('→ Detected as: Trial Balance (Vertical - Period Data)');
      return 'Trial Balance (Vertical - Period Data)';
    } else if (hasAccountNumber && hasAccountName && !hasDebit && !hasCredit && !hasDateColumn && !hasDateColumns) {
      debugLog('→ Detected as: Trial Balance (Vertical - Net Balance)');
      return 'Trial Balance (Vertical - Net Balance)';
    } else if (!hasAccountNumber && hasDateColumns) {
      debugLog('→ Detected as: P&L/Balance Sheet (Horizontal - Period Columns)');
      return 'P&L/Balance Sheet (Horizontal - Period Columns)';
    } else if (!hasAccountNumber && !hasDateColumns) {
      debugLog('→ Detected as: P&L/Balance Sheet (Vertical - Line Items)');
      return 'P&L/Balance Sheet (Vertical - Line Items)';
    } else {
      debugLog('→ Detected as: Unknown Format');
      return 'Unknown Format';
    }
  }, [debugLog]);

  // Calculate confidence score for auto-detected format (0-100)
  const detectFormatConfidence = useCallback((headers: string[], rows: string[][], detectedFormat: string | null): number => {
    if (!detectedFormat || detectedFormat === 'Unknown Format') return 0;

    const cleanedHeaders = headers.filter(h => h && h.trim() !== '');
    const lowerHeaders = cleanedHeaders.map(h => h.toLowerCase());
    const hasAccountNumber = hasAccountIdentifier(lowerHeaders, rows);
    const hasAccountName = lowerHeaders.some(h =>
      h.includes('account') && (h.includes('name') || h.includes('description'))
    );
    const hasDebit = lowerHeaders.some(h => h.includes('debit'));
    const hasCredit = lowerHeaders.some(h => h.includes('credit'));

    const datePatterns = [
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
      /^\d{4}-\d{1,2}-\d{1,2}$/,
      /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
      /^\w{3}[-\s]\d{2}$/,
      /^\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}$/,
    ];
    const dateColumnCount = cleanedHeaders.filter((h, i) => {
      if (i < 2 || !h || h.trim() === '') return false;
      return datePatterns.some(p => p.test(h.trim())) || !isNaN(Date.parse(h.trim()));
    }).length;

    let confidence = 40; // base

    // Strong structural signals
    if (hasAccountNumber && hasAccountName) confidence += 25;
    if (hasDebit && hasCredit) confidence += 15;
    if (dateColumnCount >= 3) confidence += 15;
    else if (dateColumnCount >= 1) confidence += 8;

    // Penalize ambiguous P&L vs Balance Sheet (no account numbers, hard to distinguish)
    if (!hasAccountNumber && !hasDebit && !hasCredit) {
      confidence = Math.min(confidence, 55);
    }

    return Math.min(100, confidence);
  }, []);

  // Enhanced column-based indentation analysis
  const analyzeRowStructure = useCallback((
    row: string[], 
    rowIndex: number, 
    allRows: string[][], 
    dateStartColumn: number
  ): {
    indentationLevel: number;
    accountName: string;
    fullPath: string;
    hasNumericalData: boolean;
    followedBySubtotal: boolean;
    isStructuralLineItem: boolean;
    accountColumnIndex: number;
  } => {
    
    // Find the rightmost non-empty, non-numeric cell before date columns
    let accountName = '';
    let accountColumnIndex = -1;
    let indentationLevel = 0;
    
    // Scan from right to left (most specific to least specific)
    for (let i = Math.min(row.length - 1, dateStartColumn - 1); i >= 0; i--) {
      const cell = row[i]?.toString().trim() || '';
      if (cell) {
        const cleanCell = cell.replace(/[$,\s()]/g, '');
        const isNumber = !isNaN(parseFloat(cleanCell)) && cleanCell !== '';
        
        if (!isNumber) {
          accountName = cell;
          accountColumnIndex = i;
          indentationLevel = i; // Column index IS the indentation level
          
          debugLog(`📍 Found account name "${accountName}" at column ${i} (indentation level ${indentationLevel})`);
          break;
        }
      }
    }
    
    // Check if this row has numerical data
    const hasNumericalData = row.slice(dateStartColumn).some(value => {
      if (!value || value.toString().trim() === '') return false;
      const cleanValue = value.toString().replace(/[$,\s()]/g, '');
      const numValue = parseFloat(cleanValue);
      return !isNaN(numValue) && Math.abs(numValue) > 0.01;
    });
    
    // Build hierarchical path based on ALL columns before the account name
    const pathComponents: string[] = [];
    for (let i = 0; i < accountColumnIndex; i++) {
      const cell = row[i]?.toString().trim();
      if (cell) {
        const cleanCell = cell.replace(/[$,\s()]/g, '');
        if (isNaN(parseFloat(cleanCell)) || cleanCell === '') {
          pathComponents.push(cell);
        }
      }
    }
    pathComponents.push(accountName);
    const fullPath = pathComponents.join(' > ');
    
    // Check if followed by subtotal within next 1-8 rows
    const followedBySubtotal = checkIfFollowedBySubtotalColumnBased(
      rowIndex, 
      allRows, 
      dateStartColumn, 
      accountColumnIndex
    );
    
    // Determine if this is a structural line item
    const isStructuralLineItem = hasNumericalData && 
                                followedBySubtotal && 
                                accountColumnIndex >= 0 &&
                                accountName.length > 0;
    
    debugLog(`📏 Column-based Structure Analysis for "${accountName}":`);
    debugLog(`   - Column Index: ${accountColumnIndex}`);
    debugLog(`   - Indentation Level: ${indentationLevel}`);
    debugLog(`   - Has Numerical Data: ${hasNumericalData}`);
    debugLog(`   - Followed by Subtotal: ${followedBySubtotal}`);
    debugLog(`   - Is Structural Line Item: ${isStructuralLineItem}`);
    debugLog(`   - Full Path: "${fullPath}"`);
    
    return {
      indentationLevel,
      accountName,
      fullPath,
      hasNumericalData,
      followedBySubtotal,
      isStructuralLineItem,
      accountColumnIndex
    };
  }, []);

  // Column-based subtotal detection
  const checkIfFollowedBySubtotalColumnBased = useCallback((
    currentIndex: number,
    allRows: string[][],
    dateStartColumn: number,
    currentColumnIndex: number
  ): boolean => {
    
    // Look ahead 1-8 rows for a subtotal
    for (let i = currentIndex + 1; i <= Math.min(allRows.length - 1, currentIndex + 8); i++) {
      const row = allRows[i];
      if (!row) continue;
      
      // Find the account name and its column position
      let accountName = '';
      let rowColumnIndex = -1;
      
      for (let j = Math.min(row.length - 1, dateStartColumn - 1); j >= 0; j--) {
        const cell = row[j]?.toString().trim() || '';
        if (cell) {
          const cleanCell = cell.replace(/[$,\s()]/g, '');
          const isNumber = !isNaN(parseFloat(cleanCell)) && cleanCell !== '';
          
          if (!isNumber) {
            accountName = cell;
            rowColumnIndex = j;
            break;
          }
        }
      }
      
      if (!accountName) continue;
      
      // Check if this looks like a subtotal/total
      const isTotalPattern = [
        /^total\s/i,
        /^gross\s/i,
        /^net\s/i,
        /\stotal$/i,
        /^subtotal/i
      ].some(pattern => pattern.test(accountName));
      
      // Check if this row has numerical data
      const hasNumericalData = row.slice(dateStartColumn).some(value => {
        if (!value || value.toString().trim() === '') return false;
        const cleanValue = value.toString().replace(/[$,\s()]/g, '');
        const numValue = parseFloat(cleanValue);
        return !isNaN(numValue) && Math.abs(numValue) > 0.01;
      });
      
      // Key insight: Subtotals are usually at the same level or less indented (lower column index)
      // than the line items they're summing
      if (isTotalPattern && hasNumericalData && rowColumnIndex <= currentColumnIndex) {
        debugLog(`📊 Found column-based subtotal "${accountName}" at row ${i + 1}, column ${rowColumnIndex} (current: column ${currentColumnIndex})`);
        return true;
      }
      
      // Stop if we encounter another section at a higher level (less indented)
      if (rowColumnIndex < currentColumnIndex && hasNumericalData) {
        debugLog(`🛑 Stopping search - found higher-level item at column ${rowColumnIndex}`);
        break;
      }
      
      // Also stop if we hit a major section header
      if (!hasNumericalData && rowColumnIndex <= currentColumnIndex) {
        debugLog(`🛑 Stopping search - found section header at column ${rowColumnIndex}`);
        break;
      }
    }
    
    return false;
  }, [debugLog]);

  // Helper function to check if a row mathematically sums recent rows
  const checkIfRowSumsRecentRows = useCallback((
    currentRow: string[], 
    currentIndex: number, 
    allRows: string[][], 
    dateStartColumn: number
  ): { isTotal: boolean; confidence: number } => {
    
    // Get values from current row
    const currentValues = currentRow.slice(dateStartColumn).map(value => {
      if (!value || value.toString().trim() === '') return 0;
      let cleanValue = value.toString().replace(/[$,\s]/g, '');
      
      // Handle negative values in parentheses
      let isNegative = false;
      if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
        isNegative = true;
        cleanValue = cleanValue.slice(1, -1);
      }
      if (cleanValue.startsWith('-')) {
        isNegative = true;
        cleanValue = cleanValue.substring(1);
      }
      
      let numValue = parseFloat(cleanValue);
      if (isNegative) numValue = -numValue;
      
      return isNaN(numValue) ? 0 : numValue;
    });

    let matches = 0;
    let totalChecks = 0;

    for (let colIndex = 0; colIndex < currentValues.length; colIndex++) {
      const currentValue = currentValues[colIndex];
      if (Math.abs(currentValue) < 0.01) continue; // Skip near-zero values

      let sum = 0;
      let itemCount = 0;

      // Look back through recent rows to find line items that could sum to this total
      for (let i = Math.max(0, currentIndex - 20); i < currentIndex; i++) {
        const row = allRows[i];
        if (!row || !row[dateStartColumn + colIndex]) continue;

        // Skip if this row looks like another total/subtotal
        const rowName = row[0]?.toString().toLowerCase() || '';
        if (rowName.includes('total') || rowName.includes('subtotal') || rowName.includes('gross') || rowName.includes('net')) {
          continue;
        }

        const value = row[dateStartColumn + colIndex];
        let cleanValue = value.toString().replace(/[$,\s]/g, '');
        
        let isNegative = false;
        if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
          isNegative = true;
          cleanValue = cleanValue.slice(1, -1);
        }
        if (cleanValue.startsWith('-')) {
          isNegative = true;
          cleanValue = cleanValue.substring(1);
        }
        
        let numValue = parseFloat(cleanValue);
        if (isNegative) numValue = -numValue;
        
        if (!isNaN(numValue) && Math.abs(numValue) > 0.01) {
          sum += numValue;
          itemCount++;
        }
      }

      totalChecks++;
      
      // Check if sum approximately equals current value (within 2% tolerance for financial data)
      const tolerance = Math.max(Math.abs(currentValue) * 0.02, 0.01);
      if (itemCount >= 2 && Math.abs(sum - currentValue) <= tolerance) {
        matches++;
        debugLog(`🧮 Mathematical match found in column ${colIndex}: sum=${sum.toFixed(2)}, current=${currentValue.toFixed(2)}, items=${itemCount}, tolerance=${tolerance.toFixed(2)}`);
      }
    }

    const confidence = totalChecks > 0 ? (matches / totalChecks) * 100 : 0;
    const isTotal = confidence >= 50; // Lowered threshold since financial data can be complex

    debugLog(`🧮 Math validation: ${matches}/${totalChecks} columns match (${confidence.toFixed(1)}% confidence) → ${isTotal ? 'TOTAL' : 'LINE ITEM'}`);

    return { isTotal, confidence };
  }, [debugLog]);

  // Helper function to analyze context
  const analyzeRowContext = useCallback((
    rowIndex: number, 
    allRows: string[][], 
    dateStartColumn: number
  ): { likelySubtotal: boolean; confidence: number } => {
    
    // Look at the next few rows
    let evidencePoints = 0;
    let totalPoints = 0;

    // Check if followed by blank row or another total
    const nextRow = allRows[rowIndex + 1];
    if (!nextRow || !nextRow[0]?.toString().trim()) {
      evidencePoints += 2; // Blank row after suggests this is a total
    }
    totalPoints += 2;

   // Check if the next non-blank row is clearly a different section
   for (let i = rowIndex + 1; i < Math.min(allRows.length, rowIndex + 3); i++) {
     const row = allRows[i];
     const name = row?.[0]?.toString().toLowerCase() || '';
     if (name && (name.includes('expense') || name.includes('income') || name.includes('cost'))) {
       evidencePoints += 1; // Section change suggests previous was a total
       break;
     }
   }
   totalPoints += 1;

   const confidence = totalPoints > 0 ? (evidencePoints / totalPoints) * 100 : 0;
   return { likelySubtotal: confidence > 50, confidence };
 }, []);

  // Enhanced detectRowType with column-based logic
 const detectRowType = useCallback((row: string[], rowIndex: number, allRows: string[][], dateStartColumn: number): {
   type: 'line_item' | 'subtotal' | 'section_header' | 'skip';
   confidence: number;
   accountName: string;
   fullPath: string;
   analysisReason: string;
   columnIndex: number;
 } => {
   
   const structure = analyzeRowStructure(row, rowIndex, allRows, dateStartColumn);
   
   if (!structure.accountName) {
     return { 
       type: 'skip', 
       confidence: 100, 
       accountName: '', 
       fullPath: '',
       analysisReason: 'No account name found',
       columnIndex: -1
     };
   }

   const lowerName = structure.accountName.toLowerCase();
   
   // 1. EXPLICIT SUBTOTAL/TOTAL DETECTION (Highest Confidence)
   // Update the explicit total/subtotal patterns in detectRowType:

   const explicitTotalPatterns = [
     /^total\s/i,
     /^gross\s/i,
     /^net\s/i,
     /\stotal$/i,
     /^subtotal/i,
     /^sub[\s-]total/i,
     // **ADD BALANCE SHEET SPECIFIC TOTALS**
     /^total\s+assets/i,
     /^total\s+current\s+assets/i,
     /^total\s+liabilities/i,
     /^total\s+current\s+liabilities/i,
     /^total\s+equity/i,
     /^total\s+liabilities\s+and\s+equity/i,
     /^total\s+stockholders/i,
     /^total\s+shareholders/i
   ];
   
   if (explicitTotalPatterns.some(pattern => pattern.test(structure.accountName))) {
     return { 
       type: 'subtotal', 
       confidence: 95, 
       accountName: structure.accountName, 
       fullPath: structure.fullPath,
       analysisReason: 'Explicit total/subtotal pattern match',
       columnIndex: structure.accountColumnIndex
     };
   }

   // 2. NO NUMERICAL DATA = SECTION HEADER
   if (!structure.hasNumericalData) {
     return { 
       type: 'section_header', 
       confidence: 90, 
       accountName: structure.accountName, 
       fullPath: structure.fullPath,
       analysisReason: 'No numerical data - section header',
       columnIndex: structure.accountColumnIndex
     };
   }

   // 3. COLUMN-BASED STRUCTURAL ANALYSIS (Key Innovation!)
   // Items that are followed by a subtotal are likely line items
   if (structure.isStructuralLineItem) {
     return { 
       type: 'line_item', 
       confidence: 92, 
       accountName: structure.accountName, 
       fullPath: structure.fullPath,
       analysisReason: `Column-based structural analysis - line item at column ${structure.accountColumnIndex} followed by subtotal`,
       columnIndex: structure.accountColumnIndex
     };
   }

   // 4. DEEP INDENTATION (High Column Index) + NUMERICAL DATA = Line Item
   // Items deeply indented (high column index) with data are almost always line items
   if (structure.accountColumnIndex >= 2 && structure.hasNumericalData) {
     return { 
       type: 'line_item', 
       confidence: 88, 
       accountName: structure.accountName, 
       fullPath: structure.fullPath,
       analysisReason: `Deep indentation at column ${structure.accountColumnIndex} with numerical data`,
       columnIndex: structure.accountColumnIndex
     };
   }

   // 5. MATHEMATICAL VALIDATION
   const mathCheck = checkIfRowSumsRecentRows(row, rowIndex, allRows, dateStartColumn);
   if (mathCheck.isTotal && mathCheck.confidence > 70) {
     return { 
       type: 'subtotal', 
       confidence: mathCheck.confidence, 
       accountName: structure.accountName, 
       fullPath: structure.fullPath,
       analysisReason: `Mathematical validation - sums to recent rows (${mathCheck.confidence.toFixed(1)}%)`,
       columnIndex: structure.accountColumnIndex
     };
   }

   // 6. COLUMN POSITION HEURISTICS
   // Column 0 items with numerical data could be major totals
   if (structure.accountColumnIndex === 0 && structure.hasNumericalData) {
     // But check if it's actually a line item by looking at context
     const contextAnalysis = analyzeRowContext(rowIndex, allRows, dateStartColumn);
     if (contextAnalysis.likelySubtotal && contextAnalysis.confidence > 60) {
       return { 
         type: 'subtotal', 
         confidence: contextAnalysis.confidence, 
         accountName: structure.accountName, 
         fullPath: structure.fullPath,
         analysisReason: `Column 0 item with context suggesting subtotal (${contextAnalysis.confidence.toFixed(1)}%)`,
         columnIndex: structure.accountColumnIndex
       };
     }
   }

   // 7. DEFAULT TO LINE ITEM (with column-aware confidence)
   const confidence = structure.accountColumnIndex >= 1 ? 80 : 70; // Higher confidence for indented items
   
   return { 
     type: 'line_item', 
     confidence: confidence, 
     accountName: structure.accountName, 
     fullPath: structure.fullPath,
     analysisReason: `Default classification - column ${structure.accountColumnIndex} with numerical data`,
     columnIndex: structure.accountColumnIndex
   };
 }, [analyzeRowStructure, checkIfRowSumsRecentRows, analyzeRowContext]);

 // Enhanced flow-based context detection based on financial statement structure
 const detectFlowBasedContext = useCallback((rows: string[][], fileFormat: string): {
   flowContext: Record<number, 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'>;
   flowAnchors: Array<{rowIndex: number, anchorType: string, context: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'}>;
   eliminateRows: Set<number>; // Rows to eliminate (net income on Balance Sheet)
 } => {
   const flowContext: Record<number, 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'> = {};
   const flowAnchors: Array<{rowIndex: number, anchorType: string, context: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'}> = [];
   const eliminateRows = new Set<number>();
   
   const isBalanceSheet = fileFormat?.toLowerCase().includes('balance sheet') || 
                          fileFormat?.toLowerCase().includes('trial balance');
   const isPLStatement = fileFormat?.toLowerCase().includes('p&l') || 
                         fileFormat?.toLowerCase().includes('profit') ||
                         fileFormat?.toLowerCase().includes('income');

   debugLog(`🌊 Flow-based context detection for ${fileFormat}`);

   for (let i = 0; i < rows.length; i++) {
     const row = rows[i];
     if (!row || row.length < 2) continue;
     
     const accountName = (row[1] || row[0] || '').toLowerCase().trim();
     
     // Balance Sheet Flow Detection
     if (isBalanceSheet) {
       // Assets section markers
       if (accountName.includes('total assets') || accountName.includes('total current assets')) {
         flowAnchors.push({rowIndex: i, anchorType: 'total_assets', context: 'asset'});
         debugLog(`🔵 Found Balance Sheet anchor: "Total Assets" at row ${i}`);
         // Everything after "Total Assets" should be liabilities/equity
         for (let j = i + 1; j < rows.length; j++) {
           const nextRow = rows[j];
           if (!nextRow) continue;
           const nextAccountName = (nextRow[1] || nextRow[0] || '').toLowerCase().trim();
           
           // Check for liability section
           if (nextAccountName.includes('liabilities') || 
               nextAccountName.includes('current liabilities') ||
               nextAccountName.includes('accounts payable') ||
               nextAccountName.includes('accrued') ||
               nextAccountName.includes('debt') ||
               nextAccountName.includes('loan')) {
             flowContext[j] = 'liability';
           }
           // Check for equity section (usually after liabilities)
           else if (nextAccountName.includes('equity') ||
                    nextAccountName.includes('stockholders') ||
                    nextAccountName.includes('shareholders') ||
                    nextAccountName.includes('retained earnings') ||
                    nextAccountName.includes('capital stock') ||
                    nextAccountName.includes('common stock')) {
             flowContext[j] = 'equity';
           }
           // If we find "Total Liabilities", everything after should be equity
           else if (nextAccountName.includes('total liabilities')) {
             flowAnchors.push({rowIndex: j, anchorType: 'total_liabilities', context: 'liability'});
             // Mark subsequent rows as equity
             for (let k = j + 1; k < rows.length; k++) {
               flowContext[k] = 'equity';
             }
             break;
           }
         }
       }
       
       // Current Assets section
       if (accountName.includes('current assets') && !accountName.includes('total')) {
         for (let j = i; j < rows.length; j++) {
           const nextRow = rows[j];
           if (!nextRow) continue;
           const nextAccountName = (nextRow[1] || nextRow[0] || '').toLowerCase().trim();
           
           if (nextAccountName.includes('total current assets') || 
               nextAccountName.includes('non-current') ||
               nextAccountName.includes('fixed assets')) {
             break; // End of current assets section
           }
           flowContext[j] = 'asset';
         }
       }
     }
     
     // P&L Flow Detection  
     if (isPLStatement) {
       // Revenue section (typically at the top)
       if (accountName.includes('revenue') || 
           accountName.includes('sales') || 
           accountName.includes('total revenue') ||
           accountName.includes('gross revenue')) {
         flowAnchors.push({rowIndex: i, anchorType: 'revenue_section', context: 'revenue'});
         debugLog(`🟢 Found P&L anchor: "Revenue Section" at row ${i}`);
         
         // Mark current and subsequent rows as revenue until we hit expenses/COGS
         for (let j = i; j < rows.length; j++) {
           const nextRow = rows[j];
           if (!nextRow) continue;
           const nextAccountName = (nextRow[1] || nextRow[0] || '').toLowerCase().trim();
           
           // Stop when we hit expense/COGS indicators
           if (nextAccountName.includes('expense') || 
               nextAccountName.includes('cost') ||
               nextAccountName.includes('operating expenses') ||
               nextAccountName.includes('cogs') ||
               nextAccountName.includes('cost of goods sold') ||
               nextAccountName.includes('cost of sales') ||
               nextAccountName.includes('gross profit') ||
               nextAccountName.includes('gross margin')) {
             break; // Hit expense/COGS section or gross profit
           }
           flowContext[j] = 'revenue';
         }
       }
       
       // COGS section detection
       if (accountName.includes('cogs') || 
           accountName.includes('cost of goods sold') ||
           accountName.includes('cost of sales') ||
           accountName.includes('total cost of goods sold')) {
         flowAnchors.push({rowIndex: i, anchorType: 'cogs_section', context: 'expense'});
         debugLog(`🔴 Found P&L anchor: "COGS Section" at row ${i}`);
         
         // Mark current and subsequent rows as expenses until we hit gross profit
         for (let j = i; j < rows.length; j++) {
           const nextRow = rows[j];
           if (!nextRow) continue;
           const nextAccountName = (nextRow[1] || nextRow[0] || '').toLowerCase().trim();
           
           if (nextAccountName.includes('gross profit') || 
               nextAccountName.includes('gross margin') ||
               nextAccountName.includes('total cost of goods sold') ||
               nextAccountName.includes('operating expenses') ||
               nextAccountName.includes('total operating expenses')) {
             break; // Hit gross profit or operating expenses section
           }
           flowContext[j] = 'expense';
         }
       }
       
       // Gross Profit marker (revenues above, expenses below)
       if (accountName.includes('gross profit') || accountName.includes('gross margin')) {
         flowAnchors.push({rowIndex: i, anchorType: 'gross_profit', context: 'revenue'});
         debugLog(`🟡 Found P&L anchor: "Gross Profit" at row ${i} - expenses likely below`);
         
         // Everything after gross profit is typically expenses
         for (let j = i + 1; j < rows.length; j++) {
           const nextRow = rows[j];
           if (!nextRow) continue;
           const nextAccountName = (nextRow[1] || nextRow[0] || '').toLowerCase().trim();
           
           // Skip total/summary rows
           if (nextAccountName.includes('total') || 
               nextAccountName.includes('net income') ||
               nextAccountName.includes('net profit')) {
             continue;
           }
           
           // Check for "Other Income" section (revenue after expenses)
           if (nextAccountName.includes('other income') || 
               nextAccountName.includes('non-operating income')) {
             flowAnchors.push({rowIndex: j, anchorType: 'other_income', context: 'revenue'});
             // Mark this section as revenue until we hit more expenses or totals
             for (let k = j; k < rows.length; k++) {
               const otherRow = rows[k];
               if (!otherRow) continue;
               const otherAccountName = (otherRow[1] || otherRow[0] || '').toLowerCase().trim();
               
               if (otherAccountName.includes('total') || 
                   otherAccountName.includes('net income') ||
                   otherAccountName.includes('net profit') ||
                   otherAccountName.includes('operating expenses') ||
                   otherAccountName.includes('expense')) {
                 break;
               }
               flowContext[k] = 'revenue';
             }
             break;
           }
           
           flowContext[j] = 'expense';
         }
       }
     }
     
     // Universal contra account detection
     if (accountName.includes('allowance') || 
         accountName.includes('accumulated depreciation') ||
         accountName.includes('accumulated amortization') ||
         (accountName.includes('contra') && accountName.includes('asset'))) {
       flowContext[i] = 'asset'; // Contra assets are still assets (but with credit balance)
       flowAnchors.push({rowIndex: i, anchorType: 'contra_asset', context: 'asset'});
       debugLog(`🔴 Found contra asset: "${accountName}" at row ${i}`);
     }
     
     // 🚨 CRITICAL: Net Income Elimination Detection (Balance Sheet only)
     if (isBalanceSheet && (
         accountName.includes('current period net income') ||
         accountName.includes('current period earnings') ||
         accountName.includes('net income - current period') ||
         accountName.includes('earnings - current period') ||
         accountName.includes('current year net income') ||
         accountName.includes('current year earnings') ||
         accountName.includes('ytd net income') ||
         accountName.includes('year to date net income') ||
         (accountName.includes('net income') && (
           accountName.includes('current') ||
           accountName.includes('period') ||
           accountName.includes('year') ||
           accountName.includes('ytd')
         )))) {
       // Mark for elimination to prevent double counting
       eliminateRows.add(i);
       flowAnchors.push({rowIndex: i, anchorType: 'net_income_elimination', context: 'equity'});
       debugLog(`🚨 Found Net Income elimination candidate: "${accountName}" at row ${i} - will be excluded to prevent double counting`);
     }
   }
   
   debugLog(`🌊 Flow detection complete. Found ${flowAnchors.length} anchors, ${Object.keys(flowContext).length} flow contexts`);
   flowAnchors.forEach(anchor => {
     debugLog(`   📍 ${anchor.anchorType} at row ${anchor.rowIndex} → ${anchor.context}`);
   });
   
   return { flowContext, flowAnchors, eliminateRows };
 }, [debugLog]);

 // Retained Earnings Flow Validation
 const validateRetainedEarningsFlow = useCallback((accounts: AccountEntry[]): {
   warnings: string[];
   monthlyRetainedEarnings: Record<string, number>;
 } => {
   const warnings: string[] = [];
   const monthlyRetainedEarnings: Record<string, number> = {};
   
   // Find retained earnings accounts
   const retainedEarningsAccounts = accounts.filter(account => {
     const lowerName = account.name.toLowerCase();
     return lowerName.includes('retained earnings') || 
            lowerName.includes('retained profit') ||
            lowerName.includes('accumulated earnings') ||
            (lowerName.includes('retained') && lowerName.includes('earnings'));
   });
   
   if (retainedEarningsAccounts.length === 0) {
     warnings.push('⚠️ No retained earnings account found - this may indicate missing equity accounts');
     return { warnings, monthlyRetainedEarnings };
   }
   
   // Group by month and calculate net retained earnings per month
   retainedEarningsAccounts.forEach(account => {
     const netValue = account.debit - account.credit;
     if (!monthlyRetainedEarnings[account.month]) {
       monthlyRetainedEarnings[account.month] = 0;
     }
     monthlyRetainedEarnings[account.month] += netValue;
   });
   
   // Sort months chronologically
   const months = Object.keys(monthlyRetainedEarnings).sort();
   
   if (months.length > 1) {
     debugLog(`📊 Retained earnings flow analysis across ${months.length} months:`);
     
     for (let i = 1; i < months.length; i++) {
       const currentMonth = months[i];
       const previousMonth = months[i - 1];
       const currentRE = monthlyRetainedEarnings[currentMonth];
       const previousRE = monthlyRetainedEarnings[previousMonth];
       const change = currentRE - previousRE;
       
       debugLog(`   ${previousMonth} → ${currentMonth}: $${previousRE.toFixed(2)} → $${currentRE.toFixed(2)} (Δ $${change.toFixed(2)})`);
       
       // Check for unusual mathematical changes (more than 200% change with material amounts)
       if (Math.abs(previousRE) > 100 && Math.abs(change) > Math.abs(previousRE) * 2) {
         warnings.push(`⚠️ Large retained earnings change from ${previousMonth} to ${currentMonth}: $${change.toFixed(2)} (${((change/previousRE)*100).toFixed(1)}%) - verify mathematical accuracy`);
       }
       
       // Check for impossible mathematical jumps (e.g., data entry errors)
       if (Math.abs(change) > 1000000 && Math.abs(change) > Math.abs(previousRE) * 10) {
         warnings.push(`⚠️ Extremely large retained earnings change from ${previousMonth} to ${currentMonth}: $${change.toFixed(2)} - possible data entry error`);
       }
     }
   }
   
   return { warnings, monthlyRetainedEarnings };
 }, [debugLog]);

 // In src/components/FileUpload.tsx, update the processFinancialStatementRows function:

 const processFinancialStatementRows = useCallback((rows: string[][], dateStartColumn: number, consolidationEngine: SmartAccountConsolidationEngine, fileFormat: string): {
   filteredRows: string[][];
   accountNumberMap: Record<string, string>;
   hierarchyContext: Record<string, 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'>; // Enhanced with BS contexts
   excludedRows: Array<{name: string, reason: string, confidence: number, columnIndex: number}>;
 } => {
  const lowerFormat = (fileFormat || '').toLowerCase();
  const isBalanceSheetFormat = lowerFormat.includes('balance sheet');
  const isPLFormat = lowerFormat.includes('p&l') || lowerFormat.includes('profit') || lowerFormat.includes('income');
  const defaultContext: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity' =
    isBalanceSheetFormat ? 'asset' : 'expense';

   const filteredRows: string[][] = [];
   const accountNumberMap: Record<string, string> = {};
   const hierarchyContext: Record<string, 'revenue' | 'expense' | 'asset' | 'liability' | 'equity'> = {};
   const excludedRows: Array<{name: string, reason: string, confidence: number, columnIndex: number}> = [];
  let currentContext: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity' = defaultContext;
   
   // ✨ NEW: Apply flow-based context detection
   const { flowContext, flowAnchors, eliminateRows } = detectFlowBasedContext(rows, fileFormat);
   debugLog(`🌊 Flow-based context applied to ${Object.keys(flowContext).length} rows`);
   debugLog(`🚨 Net income elimination: ${eliminateRows.size} rows marked for exclusion`);
   
   const accountNameCounts: Record<string, number> = {};

   rows.forEach((row, rowIndex) => {
     // **NEW: Skip completely blank rows**
     const isBlankRow = row.every(cell => !cell || cell.toString().trim() === '');
     if (isBlankRow) {
       debugLog(`⏭️  Skipping blank row ${rowIndex + 1}`);
       return;
     }
     
     const detection = detectRowType(row, rowIndex, rows, dateStartColumn);
     
     debugLog(`\n--- Row ${rowIndex + 1}: "${detection.accountName}" [Col ${detection.columnIndex}] ---`);
     debugLog(`Type: ${detection.type}, Confidence: ${detection.confidence}%`);
     debugLog(`Reason: ${detection.analysisReason}`);
     debugLog(`Full Path: "${detection.fullPath}"`);

     if (detection.type === 'skip') {
       debugLog(`⏭️  Skipping: ${detection.analysisReason}`);
       return;
     }

     // 🚨 CRITICAL: Net Income Elimination Check (Balance Sheet only)
     if (eliminateRows.has(rowIndex)) {
       excludedRows.push({
         name: detection.accountName,
         reason: 'Net Income elimination - prevents double counting with P&L accounts',
         confidence: 100,
         columnIndex: detection.columnIndex
       });
       debugLog(`🚨 ELIMINATED: "${detection.accountName}" - Net Income line removed to prevent double counting`);
       return;
     }

     if (detection.type === 'subtotal') {
       excludedRows.push({
         name: detection.accountName,
         reason: detection.analysisReason,
         confidence: detection.confidence,
         columnIndex: detection.columnIndex
       });
       debugLog(`❌ Excluding: ${detection.accountName} (${detection.analysisReason})`);
       return;
     }

     if (detection.type === 'section_header') {
       const lowerName = detection.accountName.toLowerCase();
       
       // **ENHANCED: Balance Sheet Section Context Detection**
       if (lowerName.includes('assets') || lowerName.includes('current assets') || 
           lowerName.includes('non-current assets') || lowerName.includes('fixed assets')) {
         currentContext = 'asset';
       } else if (lowerName.includes('liabilities') || lowerName.includes('current liabilities') ||
                 lowerName.includes('non-current liabilities') || lowerName.includes('long-term liabilities')) {
         currentContext = 'liability';
       } else if (lowerName.includes('equity') || lowerName.includes('stockholders') ||
                 lowerName.includes('shareholders') || lowerName.includes('members') ||
                 lowerName.includes('owner')) {
         currentContext = 'equity';
       }
       // Keep P&L detection for mixed files
       else if (lowerName.includes('income') || lowerName.includes('revenue') || 
               lowerName.includes('sales') || lowerName.includes('ordinary income')) {
         currentContext = 'revenue';
       } else if (lowerName.includes('expense') || lowerName.includes('cost') ||
                 lowerName.includes('payroll') || lowerName.includes('consulting') ||
                 lowerName.includes('direct costs') || lowerName.includes('operating')) {
         currentContext = 'expense';
       }
       
       debugLog(`📁 Section header: ${detection.accountName} → Context: ${currentContext}`);
       return;
     }

     // ENHANCED: Flow-based + Financial statement-aware context auto-detection
     let accountContext = currentContext;
     
     // ✨ PRIORITY 1: Check flow-based context first (most accurate)
     if (flowContext[rowIndex]) {
       accountContext = flowContext[rowIndex];
       debugLog(`🌊 Flow-based context override: Row ${rowIndex} → ${accountContext} (from flow analysis)`);
     } else {
       // PRIORITY 2: Traditional context detection as fallback
       const lowerAccountName = detection.accountName.toLowerCase();
      const isBalanceSheet = lowerFormat.includes('balance sheet') || lowerFormat.includes('trial balance');
      const isPLStatement = lowerFormat.includes('p&l') || lowerFormat.includes('profit') || lowerFormat.includes('income');
       
       debugLog(`📊 Statement type context: isBalanceSheet=${isBalanceSheet}, isPLStatement=${isPLStatement}, fileFormat="${fileFormat}"`);

      const isShortAR = /(^|\s)ar(\s|$)/i.test(detection.accountName);
      const isShortAP = /(^|\s)ap(\s|$)/i.test(detection.accountName);

      const isLiability = isShortAP ||
        lowerAccountName.includes('payable') ||
        lowerAccountName.includes('loan') ||
        lowerAccountName.includes('debt') ||
        lowerAccountName.includes('note payable') ||
        lowerAccountName.includes('mortgage') ||
        lowerAccountName.includes('accrued') ||
        lowerAccountName.includes('unearned') ||
        lowerAccountName.includes('deferred') ||
        lowerAccountName.includes('liability') ||
        lowerAccountName.includes('credit card') ||
        lowerAccountName.includes('line of credit') ||
        lowerAccountName.includes('bonds payable') ||
        lowerAccountName.includes('lease liability') ||
        lowerAccountName.includes('tax payable') ||
        lowerAccountName.includes('withholding') ||
        lowerAccountName.includes('fica') ||
        lowerAccountName.includes('unemployment') ||
        lowerAccountName.includes('workers comp') ||
        lowerAccountName.includes('pension liability') ||
        lowerAccountName.includes('warranty liability');

      const isEquity = lowerAccountName.includes('stock') ||
        lowerAccountName.includes('equity') ||
        lowerAccountName.includes('capital') ||
        lowerAccountName.includes('earnings') ||
        lowerAccountName.includes('retained') ||
        lowerAccountName.includes('member equity') ||
        lowerAccountName.includes('partners equity') ||
        lowerAccountName.includes('shareholders equity') ||
        lowerAccountName.includes('common stock') ||
        lowerAccountName.includes('preferred stock') ||
        lowerAccountName.includes('additional paid') ||
        lowerAccountName.includes('treasury stock') ||
        lowerAccountName.includes('accumulated other') ||
        lowerAccountName.includes('contributed capital') ||
        lowerAccountName.includes('paid-in capital') ||
        lowerAccountName.includes('owner equity') ||
        lowerAccountName.includes('owners equity') ||
        lowerAccountName.includes('drawing') ||
        lowerAccountName.includes('distribution');

      const isAsset = isShortAR ||
        lowerAccountName.includes('cash') ||
        lowerAccountName.includes('bank') ||
        lowerAccountName.includes('receivable') ||
        lowerAccountName.includes('inventory') ||
        lowerAccountName.includes('prepaid') ||
        lowerAccountName.includes('asset') ||
        lowerAccountName.includes('deposit') ||
        lowerAccountName.includes('investment') ||
        (isBalanceSheet && (lowerAccountName.includes('equipment') ||
                            lowerAccountName.includes('building') ||
                            lowerAccountName.includes('land') ||
                            lowerAccountName.includes('machinery') ||
                            lowerAccountName.includes('vehicle') ||
                            lowerAccountName.includes('furniture')) &&
                           !lowerAccountName.includes('expense') &&
                           !lowerAccountName.includes('cost'));

      const isExpense = lowerAccountName.includes('expense') ||
        lowerAccountName.includes('cost') ||
        lowerAccountName.includes('wages') ||
        lowerAccountName.includes('salary') ||
        lowerAccountName.includes('rent') ||
        lowerAccountName.includes('utilities') ||
        lowerAccountName.includes('supplies') ||
        lowerAccountName.includes('marketing') ||
        lowerAccountName.includes('repairs') ||
        lowerAccountName.includes('maintenance') ||
        lowerAccountName.includes('depreciation') ||
        lowerAccountName.includes('payroll') ||
        lowerAccountName.includes('benefits') ||
        (isPLStatement && (lowerAccountName.includes('equipment') ||
                           lowerAccountName.includes('vehicle') ||
                           lowerAccountName.includes('machinery') ||
                           lowerAccountName.includes('furniture'))) ||
        (lowerAccountName.includes('vehicle') && lowerAccountName.includes('expense')) ||
        (lowerAccountName.includes('equipment') &&
          (lowerAccountName.includes('expense') ||
            lowerAccountName.includes('rental') ||
            lowerAccountName.includes('lease')));

      const isRevenue = !isLiability &&
        !isEquity &&
        !isAsset &&
        (lowerAccountName.includes('sales') ||
         lowerAccountName.includes('revenue') ||
         lowerAccountName.includes('income') ||
         lowerAccountName.includes('fees') ||
         lowerAccountName.includes('service revenue') ||
         lowerAccountName.includes('consulting revenue'));

      if (isBalanceSheet) {
        if (isLiability) {
          accountContext = 'liability';
        } else if (isEquity) {
          accountContext = 'equity';
        } else if (isAsset) {
          accountContext = 'asset';
        }
      } else if (isPLStatement || isPLFormat) {
        if (isRevenue) {
          accountContext = 'revenue';
        } else if (isExpense) {
          accountContext = 'expense';
        } else if (isLiability) {
          accountContext = 'liability';
        } else if (isEquity) {
          accountContext = 'equity';
        } else if (isAsset) {
          accountContext = 'asset';
        }
      } else {
        if (isLiability) {
          accountContext = 'liability';
        } else if (isEquity) {
          accountContext = 'equity';
        } else if (isAsset) {
          accountContext = 'asset';
        } else if (isRevenue) {
          accountContext = 'revenue';
        } else if (isExpense) {
          accountContext = 'expense';
        }
      }
     } // Close the else block for flow-based vs traditional detection

     // Handle line items with enhanced duplicate resolution using column info
     let uniqueAccountKey = detection.accountName;
     if (accountNameCounts[detection.accountName]) {
       // Enhanced duplicate resolution: Use section context + column info
       const contextPrefix = accountContext.toUpperCase().substring(0, 3); // ASS, LIA, EQU, REV, EXP
       uniqueAccountKey = `${detection.accountName} (${contextPrefix}-${detection.columnIndex})`;
       debugLog(`🔄 Duplicate resolved with context: "${detection.accountName}" → "${uniqueAccountKey}"`);
     }
     
     accountNameCounts[detection.accountName] = (accountNameCounts[detection.accountName] || 0) + 1;

     if (!accountNumberMap[uniqueAccountKey]) {
       // CRITICAL FIX: Use consolidation engine for proper account number assignment
       accountNumberMap[uniqueAccountKey] = consolidationEngine.assignAccountNumber(
         uniqueAccountKey, 
         fileFormat, 
         accountContext
       );
       hierarchyContext[uniqueAccountKey] = accountContext; // Use detected context
       debugLog(`🎯 Assigned account number ${accountNumberMap[uniqueAccountKey]} to "${uniqueAccountKey}" (${accountContext})`);
     }

     const modifiedRow = [...row];
     // Update the appropriate column with the unique key
     if (detection.columnIndex >= 0) {
       modifiedRow[detection.columnIndex] = uniqueAccountKey;
     }

     filteredRows.push(modifiedRow);
     debugLog(`✅ Including: ${uniqueAccountKey} (${currentContext}) → Account ${accountNumberMap[uniqueAccountKey]}`);
     debugLog(`   Reason: ${detection.analysisReason}`);
   });

   return { filteredRows, accountNumberMap, hierarchyContext, excludedRows };
 }, [detectRowType, debugLog]);

 // FIXED: Updated removeEmptyColumnsAndMapDates function
 const removeEmptyColumnsAndMapDates = useCallback((headers: string[], rows: string[][]): {
   cleanHeaders: string[];
   cleanRows: string[][];
   dateColumnMap: Record<string, { formattedMonth: string; formattedDate: string }>;
 } => {
   const dateColumnMap: Record<string, { formattedMonth: string; formattedDate: string }> = {};
   const validColumnIndices: number[] = [];
   
   // CRITICAL FIX: Include ALL columns, not just first 3
   headers.forEach((header, index) => {
     // Check if this is a date header
     const datePatterns = [
       /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
       /^\d{4}-\d{1,2}-\d{1,2}$/,
       /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
       /^\w{3}[-\s]\d{2}$/,
       /^\d{1,2}-\w{3}-\d{2,4}$/
     ];
     
     const isDateHeader = header && (
       datePatterns.some(pattern => pattern.test(header.trim())) || 
       !isNaN(Date.parse(header))
     );
     
     // Check if this column has any data
     const hasData = rows.some(row => row[index] && row[index].toString().trim() !== '');
     
     // Include if it's a date header OR has data OR is in first 6 columns (to capture account structure)
     if (isDateHeader || hasData || index < 6) {
       validColumnIndices.push(index);
     }
   });
   
   debugLog(`Valid column indices: [${validColumnIndices.join(', ')}]`);
   debugLog(`Original headers (${headers.length}):`, headers.slice(0, 10));
   
   const cleanHeaders = validColumnIndices.map(i => headers[i]);
   const cleanRows = rows.map(row => validColumnIndices.map(i => row[i] || ''));
   
   debugLog(`Clean headers (${cleanHeaders.length}):`, cleanHeaders);
   
   // Create date column map - find ACTUAL date columns
   cleanHeaders.forEach((header, cleanIndex) => {
     if (!header || header.trim() === '') return;
     
     // Check if it's a date column
     const datePatterns = [
       /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,
       /^\d{4}-\d{1,2}-\d{1,2}$/,
       /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
       /^\w{3}[-\s]\d{2}$/,
       /^\d{1,2}-\w{3}-\d{2,4}$/
     ];
     
     const isDateHeader = datePatterns.some(pattern => pattern.test(header.trim())) || !isNaN(Date.parse(header));
     
     if (isDateHeader) {
       try {
         let parsedDate: Date | null = null;
         let formattedMonth = '';
         let formattedDate = '';
         
         // Smart date parsing for common P&L formats
         if (header.match(/^\w{3}[-\s]\d{2}$/)) {
           // Format: "Jan-22", "Feb 22", etc.
           const parts = header.split(/[-\s]/);
           const monthName = parts[0];
           const year = parts[1];
           const fullYear = year.length === 2 ? `20${year}` : year;
           parsedDate = new Date(`${monthName} 1, ${fullYear}`);
           if (!isNaN(parsedDate.getTime())) {
             const lastDay = new Date(parsedDate.getFullYear(), parsedDate.getMonth() + 1, 0);
             formattedMonth = `${monthName}-${year}`;
             formattedDate = lastDay.toLocaleDateString('en-US', {
               month: '2-digit',
               day: '2-digit', 
               year: 'numeric'
             });
           }
         } else {
           // Try standard date parsing
           parsedDate = new Date(header);
           if (!isNaN(parsedDate.getTime())) {
             const month = parsedDate.toLocaleString('en-US', { month: 'short' });
             const year = parsedDate.getFullYear().toString().slice(-2);
             formattedMonth = `${month}-${year}`;
             formattedDate = parsedDate.toLocaleDateString('en-US', {
               month: '2-digit',
               day: '2-digit',
               year: 'numeric'
             });
           }
         }
         
         if (formattedMonth && formattedDate) {
           dateColumnMap[cleanIndex.toString()] = { formattedMonth, formattedDate };
           debugLog(`Mapped clean index ${cleanIndex} "${header}" → ${formattedMonth} (${formattedDate})`);
         }
       } catch (e) {
         // Not a valid date, skip
       }
     }
   });
   
   return { cleanHeaders, cleanRows, dateColumnMap };
 }, [debugLog]);
 
 const applyAccountSignLogic = useCallback((
  accountName: string, 
  value: number, 
  context: string
): number => {
   const lowerAccountName = accountName.toLowerCase();
   
   // ✨ ENHANCED: Contra account detection with proper sign logic
   const isContraAsset = lowerAccountName.includes('allowance') || 
                         lowerAccountName.includes('accumulated depreciation') ||
                         lowerAccountName.includes('accumulated amortization') ||
                         (lowerAccountName.includes('contra') && lowerAccountName.includes('asset'));
   
   if (isContraAsset && context === 'asset') {
     // Contra assets have credit balances (opposite of normal assets)
     debugLog(`🔴 Contra asset detected: "${accountName}" → Credit balance (negative)`);
     return -Math.abs(value);
   }
   
   // Use hierarchy context to determine proper sign
   if (context === 'revenue') {
     return -Math.abs(value); // Revenue is negative in accounting system
   } else if (context === 'expense') {
     return Math.abs(value); // Expenses are positive in accounting system
   } else if (context === 'asset') {
     return Math.abs(value); // Assets are positive (debit balance)
   } else if (context === 'liability') {
     return -Math.abs(value); // Liabilities are negative (credit balance)
   } else if (context === 'equity') {
     return -Math.abs(value); // Equity is negative (credit balance)
   }
   
   // Fallback: keep sign as-is
   return value;
 }, [debugLog]);
 
 const reconcileFinancialStatement = useCallback((
   accountEntries: AccountEntry[], 
   originalRows: string[][], 
   headers: string[], 
   dateColumnMap: Record<string, { formattedMonth: string; formattedDate: string }>
 ): { isReconciled: boolean; differences: Record<string, number> } => {
   const differences: Record<string, number> = {};
   
   // Find Net Income row in original data
   const netIncomeRow = originalRows.find(row => {
     const name = row[0]?.toString().toLowerCase() || '';
     return name.includes('net income') || name.includes('net profit') || name.includes('bottom line');
   });
   
   if (!netIncomeRow) {
     debugLog('No Net Income row found for reconciliation');
     return { isReconciled: true, differences: {} };
   }
   
   // Calculate net income from processed entries for each period
   Object.values(dateColumnMap).forEach(({ formattedMonth }) => {
     const periodEntries = accountEntries.filter(entry => entry.month === formattedMonth);
     const calculatedNetIncome = periodEntries.reduce((sum, entry) => {
       return sum + (entry.debit - entry.credit);
     }, 0);
     
     // Find corresponding value in original Net Income row
     const dateColumnIndex = Object.keys(dateColumnMap).find(key => 
       dateColumnMap[key].formattedMonth === formattedMonth
     );
     
     if (dateColumnIndex && netIncomeRow[parseInt(dateColumnIndex)]) {
       const originalNetIncome = parseFloat(
         netIncomeRow[parseInt(dateColumnIndex)].toString().replace(/[$,\s()]/g, '') || '0'
       );
       
       const difference = Math.abs(calculatedNetIncome - originalNetIncome);
       if (difference > 0.01) { // Allow for rounding differences
         differences[formattedMonth] = difference;
         debugLog(`Reconciliation difference in ${formattedMonth}: Calculated=${calculatedNetIncome}, Original=${originalNetIncome}, Diff=${difference}`);
       }
     }
   });
   
   const isReconciled = Object.keys(differences).length === 0;
   debugLog(`Reconciliation ${isReconciled ? 'PASSED' : 'FAILED'}`);
   
   return { isReconciled, differences };
 }, [debugLog]);

// Helper function: Process Trial Balance data
const processTrialBalance = useCallback((
  cleanRows: string[][], 
  dateColumnMap: Record<string, {formattedMonth: string, formattedDate: string}>,
  debugLog: (message: string) => void
): AccountEntry[] => {
  debugLog(`🏦 Processing Trial Balance`);
  const entries: AccountEntry[] = [];
  
  cleanRows.forEach((row, rowIndex) => {
    const accountNumber = row[0]?.toString().trim();
    const accountName = row[1]?.toString().trim();
    
    if (!accountNumber || !accountName) return;
    
    // For Trial Balance, use original account number and name
    Object.entries(dateColumnMap).forEach(([originalIndex, { formattedMonth, formattedDate }]) => {
      const cleanIndex = parseInt(originalIndex);
      const rawValue = row[cleanIndex];
      
      if (!rawValue || rawValue === '') return;
      
      let cleanValue = rawValue.toString().trim().replace(/[$,\s]/g, '');
      if (cleanValue === '-') cleanValue = '0';
      
      let isNegative = false;
      if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
        isNegative = true;
        cleanValue = cleanValue.slice(1, -1);
      }
      
      const numericValue = parseFloat(cleanValue);
      if (isNaN(numericValue)) return;
      
      const finalValue = isNegative ? -numericValue : numericValue;
      
      entries.push({
        accountNumber: accountNumber, // Keep original TB account number
        name: accountName, // Keep original TB account name
        month: formattedMonth,
        date: formattedDate,
        debit: finalValue > 0 ? finalValue : 0,
        credit: finalValue < 0 ? Math.abs(finalValue) : 0,
        category: undefined,
        type: 'n/a' as const
      });
      
      debugLog(`Added TB entry: ${accountNumber} - ${accountName} - ${formattedMonth}: ${finalValue}`);
    });
  });
  
  return entries;
}, []);

// Helper function: Merge account entries with conflict resolution
const mergeAccountEntries = useCallback((
  existingEntry: AccountEntry,
  newValue: number,
  fileData: FileWithPreview,
  existingAccountInfo: any,
  debugLog: (message: string) => void
): { updatedEntry: AccountEntry; updatedAccountInfo: any } => {
  // Determine which data to use based on file recency
  let useNewValue = true; // Default to new value
  let mergeReason = 'processing order (fallback)';
  
  if (fileData.mostRecentDate && existingAccountInfo?.sourceFileDate) {
    // Both files have dates - use the more recent one
    if (fileData.mostRecentDate > existingAccountInfo.sourceFileDate) {
      useNewValue = true;
      mergeReason = `newer file date (${fileData.mostRecentDate.toLocaleDateString()} > ${existingAccountInfo.sourceFileDate.toLocaleDateString()})`;
    } else {
      useNewValue = false;
      mergeReason = `keeping older but more recent data (${existingAccountInfo.sourceFileDate.toLocaleDateString()} > ${fileData.mostRecentDate.toLocaleDateString()})`;
    }
  } else if (fileData.mostRecentDate && !existingAccountInfo?.sourceFileDate) {
    // New file has date, existing doesn't - use new
    useNewValue = true;
    mergeReason = `new file has date (${fileData.mostRecentDate.toLocaleDateString()}), existing doesn't`;
  } else if (!fileData.mostRecentDate && existingAccountInfo?.sourceFileDate) {
    // Existing has date, new doesn't - keep existing
    useNewValue = false;
    mergeReason = `existing file has date (${existingAccountInfo.sourceFileDate.toLocaleDateString()}), new doesn't`;
  }
  
  if (useNewValue) {
    const newDebit = newValue > 0 ? newValue : 0;
    const newCredit = newValue < 0 ? Math.abs(newValue) : 0;
    
    const updatedEntry = {
      ...existingEntry,
      debit: newDebit,
      credit: newCredit
    };
    
    // Update the source file info
    const updatedAccountInfo = { ...existingAccountInfo };
    if (updatedAccountInfo) {
      updatedAccountInfo.sourceFile = fileData.file.name;
      updatedAccountInfo.sourceFileDate = fileData.mostRecentDate;
      updatedAccountInfo.totalEntries++;
    }
    
    debugLog(`🔄 REPLACED with NEWER data: ${existingEntry.accountNumber} - ${existingEntry.name} - ${existingEntry.month}: ${newValue}`);
    debugLog(`   Reason: ${mergeReason}`);
    debugLog(`   Previous: D${existingEntry.debit}, C${existingEntry.credit} → New: D${newDebit}, C${newCredit}`);
    debugLog(`   Source: ${fileData.file.name}`);
    
    return { updatedEntry, updatedAccountInfo };
  } else {
    debugLog(`⏭️  KEEPING existing data: ${existingEntry.accountNumber} - ${existingEntry.name} - ${existingEntry.month}`);
    debugLog(`   Reason: ${mergeReason}`);
    debugLog(`   Source: ${existingAccountInfo?.sourceFile || 'unknown'}`);
    
    return { updatedEntry: existingEntry, updatedAccountInfo: existingAccountInfo };
  }
}, []);

// Helper function: Process Financial Statement (P&L/Balance Sheet)
const processFinancialStatement = useCallback((
  cleanRows: string[][],
  dateColumnMap: Record<string, {formattedMonth: string, formattedDate: string}>,
  dateStartColumn: number,
  consolidationEngine: any,
  format: string,
  fileData: FileWithPreview,
  processedAccounts: Map<string, any>,
  hierarchyContext: Record<string, any>,
  applyAccountSignLogic: (name: string, value: number, context: string) => number,
  debugLog: (message: string) => void
): AccountEntry[] => {
  // DEBUG: Log processing inputs for annual files
  debugLog(`[PROCESS DEBUG] Processing ${fileData.file.name}:`);
  debugLog(`  - Rows to process: ${cleanRows.length}`);
  debugLog(`  - Date columns: ${Object.keys(dateColumnMap).join(', ')}`);
  debugLog(`  - Periods detected: ${Object.values(dateColumnMap).map(d => d.formattedMonth).join(', ')}`);
  debugLog(`  - Format: ${format}`);
  debugLog(`📊 Processing P&L/Balance Sheet with consolidation`);
  const entries: AccountEntry[] = [];
  
  const { filteredRows } = processFinancialStatementRows(cleanRows, dateStartColumn, consolidationEngine, format);
  const lowerFormat = (format || '').toLowerCase();
  const defaultContext: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity' =
    lowerFormat.includes('balance sheet') ? 'asset' : 'expense';
  
  console.log(`[processFinancialStatement] Processing ${filteredRows.length} filtered rows for ${fileData.file.name}`);
  
  filteredRows.forEach((row, rowIndex) => {
    let accountName = '';
    for (let i = Math.min(row.length - 1, dateStartColumn - 1); i >= 0; i--) {
      const cell = row[i]?.toString().trim();
      if (cell && isNaN(parseFloat(cell.replace(/[$,\s()]/g, '')))) {
        accountName = cell;
        break;
      }
    }
    
    if (!accountName) return;
    
    // ✅ CRITICAL FIX: Use consolidation engine for duplicate resolution
    const uniqueAccountKey = consolidationEngine.generateUniqueAccountKey(
      accountName, 'expense', format // temporary context for key generation
    );
    
    // Get the ACTUAL context from hierarchyContext using the unique key
    const context = hierarchyContext[uniqueAccountKey] || hierarchyContext[accountName] || defaultContext;
    
    // Check if this account has been processed before
    let finalAccountNumber: string;
    let finalAccountName: string;
    
    if (processedAccounts.has(uniqueAccountKey)) {
      // ✅ MERGE: Use existing account number and name
      const existingAccount = processedAccounts.get(uniqueAccountKey)!;
      finalAccountNumber = existingAccount.accountNumber;
      finalAccountName = uniqueAccountKey;
      existingAccount.totalEntries++;
      
      debugLog(`🔄 MERGING duplicate account: "${accountName}" → "${uniqueAccountKey}" (Account #${finalAccountNumber})`);
    } else {
      // ✅ NEW ACCOUNT: Create new account number using consolidation engine
      finalAccountNumber = consolidationEngine.assignAccountNumber(
        uniqueAccountKey, format, context
      );
      finalAccountName = uniqueAccountKey;
      
      // Track this new account with file info
      processedAccounts.set(uniqueAccountKey, {
        accountNumber: finalAccountNumber,
        context: context,
        totalEntries: 0,
        sourceFile: fileData.file.name,
        sourceFileDate: fileData.mostRecentDate
      });
      
      debugLog(`✨ NEW account created: "${accountName}" → "${uniqueAccountKey}" (Account #${finalAccountNumber})`);
    }
    
    // Process data for all periods
    Object.entries(dateColumnMap).forEach(([originalIndex, { formattedMonth, formattedDate }]) => {
      const cleanIndex = parseInt(originalIndex);
      const rawValue = row[cleanIndex];
      
      if (!rawValue || rawValue === '') return;
      
      let cleanValue = rawValue.toString().trim().replace(/[$,\s]/g, '');
      if (cleanValue === '-') cleanValue = '0';
      
      let isNegative = false;
      if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
        isNegative = true;
        cleanValue = cleanValue.slice(1, -1);
      }
      
      const numericValue = parseFloat(cleanValue);
      if (isNaN(numericValue)) return;
      
      let finalValue = isNegative ? -numericValue : numericValue;
      finalValue = applyAccountSignLogic(finalAccountName, finalValue, context);
      
      // ✅ CRITICAL: Check for existing entry to merge values
      const existingEntryIndex = entries.findIndex(entry => 
        entry.accountNumber === finalAccountNumber && 
        entry.month === formattedMonth
      );
      
      // ✅ ENHANCED: Smart merge logic based on actual file dates
      if (existingEntryIndex >= 0) {
        const existingEntry = entries[existingEntryIndex];
        const existingAccountInfo = processedAccounts.get(uniqueAccountKey);
        
        const { updatedEntry, updatedAccountInfo } = mergeAccountEntries(
          existingEntry, finalValue, fileData, existingAccountInfo, debugLog
        );
        
        entries[existingEntryIndex] = updatedEntry;
        if (updatedAccountInfo) {
          processedAccounts.set(uniqueAccountKey, updatedAccountInfo);
        }
      } else {
        // ✅ NEW ENTRY: Create new entry
        entries.push({
          accountNumber: finalAccountNumber,
          name: finalAccountName,
          month: formattedMonth,
          date: formattedDate,
          debit: finalValue > 0 ? finalValue : 0,
          credit: finalValue < 0 ? Math.abs(finalValue) : 0,
          category: undefined,
          type: 'n/a' as const
        });
        
        debugLog(`➕ NEW entry: ${finalAccountNumber} - ${finalAccountName} - ${formattedMonth}: ${finalValue}`);
      }
    });
  });
  
  // DEBUG: Log final results
  debugLog(`[PROCESS DEBUG] Finished processing ${fileData.file.name}:`);
  debugLog(`  - Total entries created: ${entries.length}`);
  debugLog(`  - Unique accounts processed: ${processedAccounts.size}`);
  debugLog(`  - Sample entries: ${entries.slice(0, 3).map(e => `${e.accountNumber}(${e.name})-${e.month}: D${e.debit}/C${e.credit}`).join(', ')}`);
  
  return entries;
}, [mergeAccountEntries]);

// Helper function: Generate consolidation report
const generateConsolidationReport = useCallback((
  consolidatedEntries: AccountEntry[],
  processedAccounts: Map<string, any>,
  orderedFiles: FileWithPreview[],
  validateRetainedEarningsFlow: (entries: AccountEntry[]) => { warnings: string[] },
  debugLog: (message: string) => void
): void => {
  const monthsFound = [...new Set(consolidatedEntries.map(entry => entry.month))];
  const accountsCreated = [...new Set(consolidatedEntries.map(entry => entry.accountNumber))];
  const uniqueAccountNames = [...new Set(consolidatedEntries.map(entry => entry.name))];
  
  debugLog(`🎯 Smart consolidation complete:`);
  debugLog(`- Total entries: ${consolidatedEntries.length}`);
  debugLog(`- Unique account numbers: ${accountsCreated.length}`);
  debugLog(`- Unique account names: ${uniqueAccountNames.length}`);
  debugLog(`- Periods: ${monthsFound.join(', ')}`);
  debugLog(`- Processed accounts tracked: ${processedAccounts.size}`);
  
  // Debug: Show file date information
  debugLog(`📅 File recency analysis:`);
  orderedFiles.forEach(file => {
    const dateStr = file.mostRecentDate 
      ? file.mostRecentDate.toLocaleDateString() 
      : 'No date found';
    debugLog(`   - ${file.file.name}: ${dateStr} (${file.detectedFormat})`);
  });
  
  // Debug: Show merged accounts with source file information
  const mergedAccounts = Array.from(processedAccounts.entries())
    .filter(([_, data]) => data.totalEntries > 0);
  if (mergedAccounts.length > 0) {
    debugLog(`📋 Accounts that were merged (showing final data source):`);
    mergedAccounts.forEach(([name, data]) => {
      const dateStr = data.sourceFileDate 
        ? data.sourceFileDate.toLocaleDateString() 
        : 'No date';
      debugLog(`   - "${name}" (Account #${data.accountNumber}): ${data.totalEntries + 1} files → Final data from "${data.sourceFile}" (${dateStr})`);
    });
  }
  
  // Debug: Show accounts with single sources for comparison
  const singleSourceAccounts = Array.from(processedAccounts.entries())
    .filter(([_, data]) => data.totalEntries === 0);
  if (singleSourceAccounts.length > 0) {
    debugLog(`📄 Single-source accounts (no merging needed): ${singleSourceAccounts.length}`);
  }
  
  // 📊 FINAL: Validate Retained Earnings Flow
  const { warnings: retainedEarningsWarnings } = validateRetainedEarningsFlow(consolidatedEntries);
  if (retainedEarningsWarnings.length > 0) {
    debugLog(`📊 Retained Earnings Validation:`);
    retainedEarningsWarnings.forEach(warning => {
      debugLog(`   ${warning}`);
    });
    // You might want to display these warnings to the user in the UI
  }
}, []);

// Aggregate period type from all files - helper function
const aggregatePeriodType = useCallback((files: FileWithPreview[]): 'monthly' | 'quarterly' | 'annual' => {
  const periodTypes = files
    .filter(file => file.periodType && file.periodDetectionConfidence && file.periodDetectionConfidence > 50)
    .map(file => ({ type: file.periodType!, confidence: file.periodDetectionConfidence! }));
  
  if (periodTypes.length === 0) {
    console.log('📊 No reliable period type detected, defaulting to monthly');
    return 'monthly';
  }
  
  // Count occurrences and weighted by confidence
  const typeCounts = periodTypes.reduce((acc, { type, confidence }) => {
    acc[type] = (acc[type] || 0) + confidence;
    return acc;
  }, {} as Record<string, number>);
  
  // Find the type with highest weighted score
  const dominantType = Object.entries(typeCounts).reduce((a, b) => 
    typeCounts[a[0]] > typeCounts[b[0]] ? a : b
  )[0] as 'monthly' | 'quarterly' | 'annual';
  
  console.log('📊 Period type aggregation:', { typeCounts, dominantType });
  return dominantType;
}, []);

// Convert to AccountEntry format - SIMPLIFIED VERSION
 const convertToAccountEntries = useCallback((files: FileWithPreview[]): AccountEntry[] => {
  debugLog('🔧 Starting smart consolidated account processing');
  
  // Initialize consolidation engine
  const consolidationEngine = new SmartAccountConsolidationEngine(CONSOLIDATION_CONFIG);
  consolidationEngine.seedExistingAccounts(existingData);
  
  // Phase 1: Analyze account number space
  consolidationEngine.analyzeAccountNumberSpace(files);
  
  // Phase 2: Process files in proper order
  const consolidatedEntries: AccountEntry[] = [];
  const orderedFiles = consolidationEngine.determineMasterFile(files);
  
  // CRITICAL: Track processed accounts to enable merging with file recency info
  const processedAccounts = new Map<string, {
    accountNumber: string;
    context: 'revenue' | 'expense' | 'asset' | 'liability' | 'equity';
    totalEntries: number;
    sourceFile: string;
    sourceFileDate?: Date;
  }>();
  
  orderedFiles.forEach((fileData, index) => {
    if (!fileData.isValid || !fileData.fullData) {
      console.log(`[convertToAccountEntries] Skipping invalid file ${index + 1}: ${fileData.file.name}`);
      return;
    }
    
    const [headers, ...rows] = fileData.fullData;
    const format = getEffectiveFormat(fileData);
    
    console.log(`[convertToAccountEntries] Processing file ${index + 1}/${orderedFiles.length}: ${fileData.file.name}`);
    console.log(`[convertToAccountEntries] - Format: "${format}"`);
    console.log(`[convertToAccountEntries] - Headers length: ${headers.length}, Rows length: ${rows.length}`);
    debugLog(`🚀 Processing file ${fileData.file.name} with format: "${format}"`);
    
    if (format?.includes('P&L/Balance Sheet (Horizontal - Period Columns)') || 
        format?.includes('P&L') || 
        format?.includes('Balance Sheet') ||
        format?.includes('Trial Balance (Horizontal - Period Columns)')) {
      
      const { cleanHeaders, cleanRows, dateColumnMap } = removeEmptyColumnsAndMapDates(headers, rows);
      console.log(`[convertToAccountEntries] removeEmptyColumnsAndMapDates for ${fileData.file.name}:`);
      console.log(`  - Original rows: ${rows.length}, Clean rows: ${cleanRows.length}`);
      console.log(`  - Date column map:`, Object.keys(dateColumnMap));
      const dateColumnIndices = Object.keys(dateColumnMap).map(k => parseInt(k));
      const dateStartColumn = dateColumnIndices.length > 0 ? Math.min(...dateColumnIndices) : 6;
      
      if (format?.includes('Trial Balance')) {
        // Process Trial Balance using helper function
        const trialBalanceEntries = processTrialBalance(cleanRows, dateColumnMap, debugLog);
        consolidatedEntries.push(...trialBalanceEntries);
      } else {
        // Process P&L/Balance Sheet using helper function
        console.log(`[convertToAccountEntries] Before processFinancialStatementRows for ${fileData.file.name}: ${cleanRows.length} clean rows`);
        const { filteredRows, accountNumberMap, hierarchyContext } = processFinancialStatementRows(cleanRows, dateStartColumn, consolidationEngine, format);
        console.log(`[convertToAccountEntries] After processFinancialStatementRows for ${fileData.file.name}: ${filteredRows.length} filtered rows`);
        
        const financialStatementEntries = processFinancialStatement(
          cleanRows, dateColumnMap, dateStartColumn, consolidationEngine, format, 
          fileData, processedAccounts, hierarchyContext, applyAccountSignLogic, debugLog
        );
        console.log(`[convertToAccountEntries] processFinancialStatement returned ${financialStatementEntries.length} entries for ${fileData.file.name}`);
        consolidatedEntries.push(...financialStatementEntries);
      }
    }
  });
  
  // Generate consolidation report using helper function
  generateConsolidationReport(consolidatedEntries, processedAccounts, orderedFiles, validateRetainedEarningsFlow, debugLog);
  
  console.log(`[convertToAccountEntries] FINAL RESULT: ${consolidatedEntries.length} total account entries`);
  
  // Debug: Check for duplicates in the final result
  const accountKeys = consolidatedEntries.map(entry => `${entry.accountNumber}-${entry.month}`);
  const uniqueKeys = new Set(accountKeys);
  if (accountKeys.length !== uniqueKeys.size) {
    console.log(`[convertToAccountEntries] WARNING: Found ${accountKeys.length - uniqueKeys.size} duplicate account-month combinations in final result`);
    
    // Group duplicates
    const keyCount = new Map<string, number>();
    accountKeys.forEach(key => {
      keyCount.set(key, (keyCount.get(key) || 0) + 1);
    });
    
    const duplicates = Array.from(keyCount.entries()).filter(([, count]) => count > 1);
    console.log(`[convertToAccountEntries] Duplicate keys:`, duplicates.slice(0, 5)); // Show first 5
  }
  
  return consolidatedEntries;
}, [debugLog, existingData, removeEmptyColumnsAndMapDates, processFinancialStatementRows, applyAccountSignLogic, detectFlowBasedContext, validateRetainedEarningsFlow, processTrialBalance, processFinancialStatement, generateConsolidationReport]);

 // Parse file
 const parseFile = useCallback((file: File): Promise<FileWithPreview> => {
   return new Promise((resolve) => {
     debugLog('Parsing file:', file.name);
     
     Papa.parse(file, {
       header: false,
       skipEmptyLines: true,
       complete: (results) => {
         const data = results.data as string[][];
         const errors: string[] = [];
         
         if (data.length === 0) {
           errors.push('File appears to be empty');
         }
         
         if (data.length > 0 && data[0].length < 2) {
           errors.push('File must have at least 2 columns');
         }
         
         // Find the actual header row (important for P&L files with title rows)
         const { headerRow, dataRows, headerRowIndex } = findActualHeaderRow(data);
         const detectedFormat = data.length > 0 ? detectFormat(headerRow, dataRows) : null;
         const formatConfidence = data.length > 0 ? detectFormatConfidence(headerRow, dataRows, detectedFormat) : 0;

         debugLog(`Header detected at row ${headerRowIndex}:`, headerRow);
         debugLog(`Format confidence: ${formatConfidence}%`);

         // Auto-select file type when confidence is high enough
         let autoSelectedType: FinancialFileType | null = null;
         if (formatConfidence >= 80 && detectedFormat) {
           if (detectedFormat.includes('Trial Balance')) autoSelectedType = 'trial_balance';
           else if (detectedFormat.includes('P&L')) autoSelectedType = 'profit_loss';
           else if (detectedFormat.includes('Balance Sheet')) autoSelectedType = 'balance_sheet';
         }

         const preview = data.slice(0, 11);
         // CRITICAL FIX: Use correct header row and data rows
         const fullData = [headerRow, ...dataRows];

         resolve({
           file,
           id: `${file.name}-${Date.now()}`,
           preview,
           fullData,
           detectedFormat,
           isValid: errors.length === 0,
           errors,
           rowCount: data.length - 1,
           columnCount: headerRow.length,
           userSelectedType: autoSelectedType,
           formatConfidence,
         });
       },
       error: (error) => {
         resolve({
           file,
           id: `${file.name}-${Date.now()}`,
           preview: null,
           fullData: null,
           detectedFormat: null,
           isValid: false,
           errors: [`Parse error: ${error.message}`],
           rowCount: 0,
           columnCount: 0,
           userSelectedType: null,
           formatConfidence: 0,
         });
       }
     });
   });
 }, [detectFormat, detectFormatConfidence, debugLog]);

 // Handle file selection
 const handleFiles = useCallback(async (fileList: FileList) => {
   const csvFiles = Array.from(fileList).filter(file => 
     file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')
   );
   
   if (csvFiles.length === 0) {
     alert('Please select CSV files only');
     return;
   }
   
   if (csvFiles.length > 10) {
     alert('Maximum 10 files allowed per upload');
     return;
   }
   
   setIsProcessing(true);
   
   try {
     const parsedFiles = await Promise.all(csvFiles.map(parseFile));
     setFiles(prev => [...prev, ...parsedFiles]);
   } catch (error) {
     alert('Error processing files. Please try again.');
   } finally {
     setIsProcessing(false);
   }
 }, [parseFile]);

 // Drag and drop handlers
 const handleDragEnter = useCallback((e: React.DragEvent) => {
   e.preventDefault();
   e.stopPropagation();
   dragCounter.current++;
   setIsDragging(true);
 }, []);

 const handleDragLeave = useCallback((e: React.DragEvent) => {
   e.preventDefault();
   e.stopPropagation();
   dragCounter.current--;
   if (dragCounter.current === 0) {
     setIsDragging(false);
   }
 }, []);

 const handleDragOver = useCallback((e: React.DragEvent) => {
   e.preventDefault();
   e.stopPropagation();
 }, []);

 const handleDrop = useCallback((e: React.DragEvent) => {
   e.preventDefault();
   e.stopPropagation();
   setIsDragging(false);
   dragCounter.current = 0;
   
   const droppedFiles = e.dataTransfer.files;
   if (droppedFiles.length > 0) {
     handleFiles(droppedFiles);
   }
 }, [handleFiles]);

 const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
   if (e.target.files && e.target.files.length > 0) {
     handleFiles(e.target.files);
   }
 }, [handleFiles]);

 const removeFile = useCallback((fileId: string) => {
   setFiles(
    prev => prev.filter(f => f.id !== fileId));
  }, []);

  const updateFileType = useCallback((fileId: string, selectedType: FinancialFileType) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, userSelectedType: selectedType } : f
    ));
  }, []);

  // Step management handlers
  const handleProcessFiles = () => {
    console.log(`[FileUpload] Processing ${files.length} files:`, files.map(f => ({
      name: f.file.name,
      format: f.detectedFormat,
      valid: f.isValid,
      rows: f.rowCount,
      cols: f.columnCount
    })));
    setCurrentStep('processing');
  };
 
  const handleProcessingSuccess = (data: any) => {
    const validFiles = files.filter(f => f.isValid);
    console.log(`[handleProcessingSuccess] Processing ${validFiles.length} valid files:`, validFiles.map(f => ({
      name: f.file.name,
      format: f.detectedFormat,
      rowCount: f.rowCount,
      fullDataLength: f.fullData?.length
    })));
    const accountEntries = convertToAccountEntries(validFiles);
    console.log(`[handleProcessingSuccess] convertToAccountEntries returned ${accountEntries.length} account entries`);
    const aggregatedPeriodType = aggregatePeriodType(validFiles);
    setDetectedPeriodType(aggregatedPeriodType);
    
    if (mode === 'additional' && existingData.length > 0) {
      // Analyze conflicts for additional data mode
      const conflicts = analyzeDataConflicts(existingData, accountEntries);
      
      if (conflicts.totalConflicts > 0) {
        setConflictAnalysis(conflicts);
        setProcessedData({
          accountEntries,
          statistics: data // Include the statistics from API validation
        });
        setCurrentStep('conflicts');
        return;
      }
      
      // CRITICAL FIX: No conflicts in additional mode - merge data automatically
      console.log('[FileUpload] No conflicts detected - automatically merging with existing data');
      const mergedData = mergeDataWithConflictResolution(existingData, accountEntries, {});
      setProcessedData({
        accountEntries: mergedData,
        statistics: data // Include the statistics from API validation
      });
      setCurrentStep('success');
      
      // DO NOT save here - let handleContinueToApp handle the saving to avoid double-save
      console.log('[FileUpload] Merged data prepared - will save when user clicks Continue to App');
      return;
    }
    
    // Initial mode (no existing data) - proceed normally
    setProcessedData({
      accountEntries,
      statistics: data // Include the statistics from API validation
    });
    setCurrentStep('success');
    
    // DO NOT save here for initial mode either - let handleContinueToApp handle the saving to avoid inconsistency
    console.log('[FileUpload] Data prepared for initial mode - will save when user clicks Continue to App');
  };
 
  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setFiles([]);
  };
 
  const handleContinueToApp = () => {
    // Use the already processed data instead of reprocessing files
    const accountEntries = processedData?.accountEntries || processedData || [];
    console.log(`[FileUpload] Continuing to app with ${accountEntries.length} account entries`, {
      mode,
      totalEntries: accountEntries.length,
      months: [...new Set(accountEntries.map((entry: AccountEntry) => entry.month))],
      accounts: [...new Set(accountEntries.map((entry: AccountEntry) => entry.accountNumber))].length
    });
    debugLog('Final account entries being passed to appropriate handler:', accountEntries);
    
    // Validate that we have financial data before proceeding
    if (!accountEntries || accountEntries.length === 0) {
      alert('No financial data was processed from the uploaded files. Please check your file format and try again.');
      return;
    }
    
    // CRITICAL FIX: Use the correct handler based on mode
    // Use the period type detected during processing
    
    if (mode === 'additional' && onDataProcessed) {
      // Additional data mode - processedData contains the merged data from handleProcessingSuccess
      // We need to save this merged data (not just the new accountEntries)
      console.log('[FileUpload] Additional data mode - saving merged data from processedData:', accountEntries.length);
      onDataProcessed(accountEntries, true, detectedPeriodType); // Mark as already merged to avoid double-merge in backend
    } else {
      // Initial data mode - use onDataLoaded to replace all data
      console.log('[FileUpload] Initial data mode - calling onDataLoaded to set new data with period type:', detectedPeriodType);
      onDataLoaded(accountEntries, detectedPeriodType);
    }
    
    // Close modal if this is additional data mode
    if (onModalClose) {
      onModalClose();
    }
  };

  // Conflict resolution handlers
  const handleConflictResolution = (resolutions: { [key: string]: 'keep_existing' | 'use_new' }) => {
    if (processedData && existingData) {
      const accountEntries = processedData?.accountEntries || processedData;
      const mergedData = mergeDataWithConflictResolution(existingData, accountEntries, resolutions);
      debugLog('Merged data after conflict resolution:', mergedData);
      
      // Update processedData with the merged result so handleContinueToApp can use it
      setProcessedData({
        accountEntries: mergedData,
        statistics: processedData?.statistics || {} // Preserve statistics
      });
      setCurrentStep('success');
      
      // DO NOT save here - let handleContinueToApp handle the saving to avoid double-save
      console.log('[FileUpload] Merged data after conflict resolution - will save when user clicks Continue to App');
    }
  };

  const handleConflictCancel = () => {
    setCurrentStep('upload');
    setFiles([]);
    setConflictAnalysis(null);
    setProcessedData(null);
  };
 
  return (
    <div className="max-w-6xl mx-auto">
      {/* Project Name */}
      {selectedProject && (
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Project: {selectedProject.name}</h1>
        </div>
      )}
 
      {/* Progress Stepper */}
      <ProgressStepper currentStep={getStepNumber()} mode={mode} />
      
      {/* Step 1: Upload */}
      {currentStep === 'upload' && (
        <div className="space-y-6">
          {/* Upload Zone */}
          <div className="flex justify-center">
            <div
              className={`
                relative border-3 border-dashed rounded-xl p-8 text-center transition-all duration-200 max-w-2xl w-full flex items-center justify-center
                ${isDragging 
                  ? 'border-blue-500 bg-blue-50 shadow-lg transform scale-[1.02]' 
                  : 'border-blue-400 bg-gradient-to-br from-blue-50 to-indigo-50 hover:border-blue-500 hover:bg-blue-100'
                }
                ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer shadow-md hover:shadow-lg'}
              `}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className={`p-4 rounded-full ${isDragging ? 'bg-blue-200' : 'bg-blue-100'} shadow-inner`}>
                  <Upload className={`w-10 h-10 ${isDragging ? 'text-blue-700' : 'text-blue-600'}`} />
                </div>
                
                <div className="space-y-2">
                  <h2 className="text-xl font-bold text-gray-800">
                    {mode === 'additional' 
                      ? 'Upload additional financial data'
                      : 'Upload your CSV files - we\'ll detect everything automatically.'
                    }
                  </h2>
                  <p className="text-sm text-gray-600">No formatting required!</p>
                  <p className="text-sm text-gray-600 text-center">
                    {mode === 'additional'
                      ? 'Add more months or update existing data. We\'ll check for conflicts and let you review changes.'
                      : 'Drag and drop your CSV files here, or click to browse and select files'
                    }
                  </p>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    disabled={isProcessing}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md text-sm font-medium disabled:bg-gray-400"
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span>Browse Files</span>
                  </button>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv"
                onChange={handleFileInput}
                className="hidden"
                disabled={isProcessing}
              />
            </div>
          </div>

 
 
          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-3">
              {files.map((fileData) => (
                <div key={fileData.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium text-gray-900">{fileData.file.name}</p>
                        <p className="text-sm text-gray-500">
                          {(fileData.file.size / 1024).toFixed(1)} KB
                          <span className="ml-2 font-medium text-blue-700">
                            {fileData.rowCount} rows × {fileData.columnCount} columns
                          </span>
                        </p>
                        {/* File Type Selector */}
                        <div className="mt-2 flex items-center flex-wrap gap-2">
                          <label className="text-xs text-gray-500 font-medium">File Type:</label>
                          <select
                            value={fileData.userSelectedType || ''}
                            onChange={(e) => updateFileType(fileData.id, e.target.value as FinancialFileType)}
                            className={`text-xs px-2 py-1 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              !fileData.userSelectedType
                                ? 'border-amber-400 bg-amber-50'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            <option value="" disabled>Select file type...</option>
                            <option value="trial_balance">Trial Balance</option>
                            <option value="profit_loss">P&L Statement</option>
                            <option value="balance_sheet">Balance Sheet</option>
                          </select>

                          {/* Auto-detection suggestion badge */}
                          {fileData.detectedFormat && fileData.detectedFormat !== 'Unknown Format' && (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                              fileData.formatConfidence >= 80
                                ? 'bg-green-100 text-green-700'
                                : fileData.formatConfidence >= 50
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-amber-100 text-amber-700'
                            }`}>
                              Suggested: {fileData.detectedFormat.split(' (')[0]}
                              <span className="ml-1 opacity-75">({fileData.formatConfidence}%)</span>
                            </span>
                          )}

                          {/* Low confidence / no selection warning */}
                          {!fileData.userSelectedType && (
                            <span className="inline-flex items-center text-xs text-amber-600">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Please select file type
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {fileData.isValid ? (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Valid</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-red-600">
                          <AlertCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Invalid</span>
                        </div>
                      )}
                      
                      <button
                        onClick={() => setPreviewModal({ isOpen: true, fileData })}
                        className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 hover:border-blue-400 rounded transition-colors"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </button>
                      
                      <button
                        onClick={() => removeFile(fileData.id)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                      </div>
                 </div>
 
                 {/* Error Messages */}
                 {fileData.errors.length > 0 && (
                   <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
                     <div className="flex">
                       <AlertCircle className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" />
                       <div>
                         <h4 className="text-sm font-medium text-red-800 mb-1">Validation Errors:</h4>
                         <ul className="text-sm text-red-700 space-y-1">
                           {fileData.errors.map((error, index) => (
                             <li key={index} className="flex items-start">
                               <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                               {error}
                             </li>
                           ))}
                         </ul>
                       </div>
                     </div>
                   </div>
                 )}
               </div>
             ))}
           </div>
         )}
 
 
         {/* Process Files Button - Centered above help */}
         {files.length > 0 && (
           <div className="flex flex-col items-center">
             <button
               onClick={handleProcessFiles}
               disabled={
                 files.filter(f => f.isValid).length === 0 ||
                 files.filter(f => f.isValid).some(f => !f.userSelectedType)
               }
               className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors shadow-md"
             >
               Process and Continue ({files.filter(f => f.isValid).length} {files.filter(f => f.isValid).length === 1 ? 'file' : 'files'})
               <ArrowRight className="w-5 h-5 ml-2" />
             </button>
             {files.filter(f => f.isValid).some(f => !f.userSelectedType) && (
               <p className="text-xs text-amber-600 mt-2">
                 Please select a file type for all files before processing
               </p>
             )}
           </div>
         )}

         {/* File Format Help - Minimized */}
         <div className="flex items-center justify-center">
           <button
             onClick={() => setShowFileFormatsHelp(!showFileFormatsHelp)}
             className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors"
           >
                         <div className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
              
            </div>
             <span>Sample Files and Setup Guide</span>
             <ArrowRight className={`w-4 h-4 transition-transform ${showFileFormatsHelp ? 'rotate-90' : ''}`} />
           </button>
         </div>

         {/* Collapsible File Formats Help */}
         {showFileFormatsHelp && (
           <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200">

           <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
             <h3 className="text-lg font-semibold text-gray-700 mb-2">Supported File Formats</h3>
             <p className="text-sm text-gray-600 mb-4">Click on each file type to download a sample file</p>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 relative">
               <div 
                 className="relative flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                 onClick={() => setSelectedFileType('trial-balance')}
               >
                 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                 <span className="text-sm"><strong>Trial Balance (CSV)</strong></span>
                 <Eye className="w-4 h-4 text-gray-400 ml-auto" />
               </div>
               <div 
                 className="relative flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                 onClick={() => setSelectedFileType('pl-statement')}
               >
                 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                 <span className="text-sm"><strong>P&L Statement (CSV)</strong></span>
                 <Eye className="w-4 h-4 text-gray-400 ml-auto" />
               </div>
               <div 
                 className="relative flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                 onClick={() => setSelectedFileType('balance-sheet')}
               >
                 <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                 <span className="text-sm"><strong>Balance Sheet (CSV)</strong></span>
                 <Eye className="w-4 h-4 text-gray-400 ml-auto" />
               </div>
             </div>
           </div>
           
           <div className="flex items-center justify-center space-x-8 text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
             <div className="flex items-center space-x-2">
             <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Maximum 10 files</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>10MB each</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Automatic format detection</span>
            </div>
          </div>
          
          {/* Need Help Link */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowHelpModal(true)}
              className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-4 py-2 rounded-lg transition-colors border border-blue-200"
            >
              <span>Need help? View step-by-step instructions</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          </div>
        )}

        {/* Centered Screenshot Preview Modal */}
        {selectedFileType && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setSelectedFileType(null)}
          >
            <div 
              className="bg-white rounded-lg p-6 shadow-2xl max-w-4xl max-h-[90vh] overflow-auto relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedFileType(null)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close preview"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
              
              <div className="text-center mb-4 pr-10">
                <h3 className="text-lg font-semibold text-gray-800">
                  {selectedFileType === 'trial-balance' && 'Trial Balance Example'}
                  {selectedFileType === 'pl-statement' && 'P&L Statement Example'}
                  {selectedFileType === 'balance-sheet' && 'Balance Sheet Example'}
                </h3>
                <p className="text-sm text-gray-600">Example format for CSV upload</p>
              </div>
              <img 
                src={
                  selectedFileType === 'trial-balance' ? restaurantTrialBalanceImg :
                  selectedFileType === 'pl-statement' ? restaurantPLImg :
                  restaurantBalanceSheetImg
                }
                alt={
                  selectedFileType === 'trial-balance' ? 'Trial Balance Example' :
                  selectedFileType === 'pl-statement' ? 'P&L Statement Example' :
                  'Balance Sheet Example'
                }
                className="w-full h-auto rounded-lg border border-gray-200 shadow-sm"
              />
              
              {/* Download Sample File Button */}
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    if (selectedFileType === 'trial-balance') {
                      link.href = '/sample_restaurant_trial_balance.csv';
                      link.download = 'sample_trial_balance.csv';
                    } else if (selectedFileType === 'pl-statement') {
                      link.href = '/sample_restaurant_p&l.csv';
                      link.download = 'sample_pl_statement.csv';
                    } else if (selectedFileType === 'balance-sheet') {
                      link.href = '/sample_restaurant_bs.csv';
                      link.download = 'sample_balance_sheet.csv';
                    }
                    link.click();
                  }}
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Sample {
                    selectedFileType === 'trial-balance' ? 'Trial Balance' :
                    selectedFileType === 'pl-statement' ? 'P&L Statement' :
                    'Balance Sheet'
                  } CSV</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal - Step-by-step Instructions */}
        {showHelpModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowHelpModal(false)}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-auto relative w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setShowHelpModal(false)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
                aria-label="Close help"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
              
              <div className="p-8 pr-16">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Step-by-Step File Upload Guide</h2>
                
                {/* What is a Trial Balance */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">What is a Trial Balance?</h3>
                  <p className="text-gray-700 mb-4">
                    A trial balance lists all of your general ledger accounts and their balances for a given period. It includes both:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mb-4 ml-4 space-y-2">
                    <li><strong>Balance Sheet accounts</strong> (assets, liabilities, equity)</li>
                    <li><strong>Profit & Loss accounts</strong> (income and expenses)</li>
                  </ul>
                  <p className="text-gray-700 mb-4">
                    It's the most complete format for us to process your financials accurately.
                  </p>
                </div>

                {/* Formatting Requirements */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Formatting Requirements</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                    <li><strong>Monthly format is required</strong> for best results.</li>
                    <li><strong>Balance Sheet accounts</strong> should be reported as of the last day of each month.</li>
                    <li><strong>Profit & Loss accounts</strong> should show monthly activity only (not year-to-date totals).</li>
                    <li><strong>Horizontal format</strong> (months as columns) is recommended for the most accurate results.</li>
                    <li>We also support uploading separate P&L and Balance Sheet files if you don't have a combined trial balance.</li>
                  </ul>
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> In most trial balances, revenues/income, liabilities, and equity accounts appear as negative values because they are credit balances — this is normal, no need to change them.
                    </p>
                  </div>
                </div>

                {/* How to Export */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6">How to Export Your Trial Balance</h3>
                  
                  {/* QuickBooks Online */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">QuickBooks Online</h4>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                      <li>Go to <strong>Reports</strong>.</li>
                      <li>Search for <strong>Trial Balance</strong>.</li>
                      <li>Set the date range to the month(s) you need.</li>
                      <li>Choose <strong>Months</strong> as the column option.</li>
                      <li>Click <strong>Export → Export to Excel (XLSX) or CSV</strong>.</li>
                    </ol>
                  </div>

                  {/* Xero */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Xero</h4>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                      <li>Go to <strong>Reports → Accounting → Trial Balance</strong>.</li>
                      <li>Select your desired month range.</li>
                      <li>Choose <strong>Show by Month</strong> in the display options.</li>
                      <li>Export as <strong>Excel or CSV</strong>.</li>
                    </ol>
                  </div>

                  {/* Excel Manual */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-800 mb-3">Excel (Manual)</h4>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                      <li><strong>First column:</strong> Account codes.</li>
                      <li><strong>Second column:</strong> Account names.</li>
                      <li>Add monthly columns with balances for each account.</li>
                      <li><strong>Balance Sheet accounts</strong> = month-end balances; <strong>P&L accounts</strong> = monthly totals only.</li>
                      <li>Save as <strong>.csv</strong>.</li>
                    </ol>
                  </div>
                </div>

                {/* Close button at bottom */}
                <div className="flex justify-center pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowHelpModal(false)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Got it, thanks!
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Processing Indicator */}
        {isProcessing && (
          <div className="flex items-center justify-center space-x-3 p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="text-lg text-blue-700 font-medium">Processing files...</span>
          </div>
        )}
 
        {/* Debug information now logged to console for auditing */}
      </div>
    )}
 
    {/* Step 2: Processing */}
    {currentStep === 'processing' && (
      <ProcessingStep
        files={files.filter(f => f.isValid)}
        onSuccess={handleProcessingSuccess}
        onBack={handleBackToUpload}
      />
    )}
 
    {/* Step 3: Conflict Resolution (Additional Mode Only) */}
    {currentStep === 'conflicts' && conflictAnalysis && (
      <ConflictResolution
        conflictAnalysis={conflictAnalysis}
        onResolve={handleConflictResolution}
        onCancel={handleConflictCancel}
      />
    )}

    {/* Step 3/4: Success */}
    {currentStep === 'success' && processedData && (
      <SuccessStep
        processedData={processedData}
        detectedPeriodType={detectedPeriodType}
        onContinue={handleContinueToApp}
      />
    )}
 
    {/* Preview Modal */}
    <PreviewModal
      isOpen={previewModal.isOpen}
      fileData={previewModal.fileData}
      onClose={() => setPreviewModal({ isOpen: false, fileData: null })}
    />
  </div>
 );
 }